import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createJob, updateJob, clearCurrentJob, refreshJobs, refreshArchiveJobs } from '../../redux/features/jobSlice';
import { getWorkers } from '../../redux/features/workerSlice';
import { getPartners } from '../../redux/features/partnerSlice';
import { getTeams } from '../../redux/features/teamSlice';
import { createClientAsync, fetchClientsAsync } from '../../redux/features/clientSlice';
import { toast } from 'react-toastify';
import { getJobFormConfig, getJobFormInitialState } from '../../utils/formConfig';
import { getBusinessType } from '../../utils/businessTypeUtils';
import Select from 'react-select';
import FieldRenderer from './job/FieldRenderer';
import PhoneCropPreview from '../common/PhoneCropPreview';
import JobFormSection from './job/JobFormSection';
import DatePickerField from './job/DatePickerField';
import TimePickerField from './job/TimePickerField';
import { getSpareParts, createSparePart } from '../../redux/features/sparePartSlice';
import { COUNTRY_DATA } from '../../utils/countryData';
import { checkJobOverlap, formatTimeSlot, addHoursToTime } from '../../utils/timeSlotUtils';
import { extractJobDataFromMessage, extractJobDataFromImages, fetchDeviceCategories, fetchServicesList, createDeviceCategory, createServiceItem, fetchServiceArrivalMethods, createServiceArrivalMethod } from '../../redux/api';
import { confirmOpenWithdrawnItemsBeforeJobClose } from '../../utils/checkOpenWithdrawnItemsForJob';
import { getDeadlineMarker } from '../../utils/deadlineMarkerUtils';
import DeadlineMarker from '../job/DeadlineMarker';
import { getMaxJobImages, isFeatureAllowed, getPackageRestrictionMessage } from '../../utils/packageLimits';
import { mergeAuthProfile } from '../../utils/mergeAuthProfile';
import {
  isOutgoingHandoffAssignmentLocked,
  isLinkedServicePartnerAssignment,
  getPartnerFromAssignedTo
} from '../../utils/handoffWorkflowUtils';
import {
  buildWorkerAssignmentOptions,
  buildTeamAssignmentOptions,
  isAssignmentAvailableOnDate
} from '../../utils/workerAvailabilityUtils';
import {
  normalizePhoneForSave,
  aiPhonesMismatch,
  isLikelyWrongAiPhone,
  isPhoneStructurallyValid
} from '../../utils/phoneValidation';

// Phone prefixes mapping
const COUNTRY_PREFIXES = {
  rs: '381', // Serbia
  ba: '387', // Bosnia and Herzegovina
  hr: '385', // Croatia
  me: '382', // Montenegro
  mk: '389', // North Macedonia
  si: '386'  // Slovenia
};

const SECTION_KEYS = ['clientInfo', 'deviceInfo', 'serviceInfo', 'additionalInfo'];
const ACCORDION_KEYS = [...SECTION_KEYS, 'legalDeadlines'];

/** AI ekstrakcija u formi posla — max slika (isti limit kao na backendu) */
const MAX_AI_JOB_IMAGES = 4;

/** YYYY-MM-DD → DD.MM.YYYY (evropski prikaz u tabeli AI) */
const formatIsoYmdToEuropean = (ymd) => {
  if (!ymd || typeof ymd !== 'string') return '—';
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd;
  return `${m[3]}.${m[2]}.${m[1]}`;
};

const extractFieldValue = (jobData, fieldName) => {
  if (!jobData) return undefined;
  if (fieldName === 'clientAddress') {
    return jobData.clientAddress;
  }
  if (fieldName === 'serviceDateTime') {
    return jobData.serviceDateTime;
  }
  if (fieldName === 'deviceType') {
    return jobData.deviceCategoryId || jobData.deviceTypeId || jobData.deviceType || '';
  }
  if (fieldName === 'serviceId') {
    return jobData.serviceId || '';
  }
  return jobData[fieldName];
};

const hasTruthyContent = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object') {
    return Object.values(value).some((val) => hasTruthyContent(val));
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }
  if (typeof value === 'boolean') {
    return value === true;
  }
  return Boolean(value);
};

const fieldHasContent = (fieldName, value) => {
  if (fieldName === 'serviceDateTime') {
    if (!value || typeof value !== 'object') return false;
    return Boolean(value.date);
  }
  if (fieldName === 'serviceLocation') {
    if (!value) return false;
    return value !== 'OnSite';
  }
  if (fieldName === 'clientAddress') {
    if (!value) return false;
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (typeof value === 'object') {
      return Object.values(value).some((val) => hasTruthyContent(val));
    }
    return false;
  }
  return hasTruthyContent(value);
};

const JobForm = ({ isEdit = false, jobData: initialJobData, onClose, className = '', isModal = false, selectedSlot = null }) => {
  const { t } = useTranslation();
  const businessType = getBusinessType();
  const baseFormConfig = useMemo(() => getJobFormConfig(businessType), [businessType]);
  
  // Helper function to parse address string into object
  // Address format in DB is typically: "street number floor apartment" (space-separated)
  // Example: "Bulevar kralja Aleksandra 15 2 5" -> street: "Bulevar kralja Aleksandra", number: "15", floor: "2", apartment: "5"
  // Example: "Sime Igumanova 5 St." -> street: "Sime Igumanova", number: "5", floor: "", apartment: "St."
  const parseAddressString = (addressString) => {
    if (!addressString || typeof addressString !== 'string') {
      return { street: '', number: '', floor: '', apartment: '' };
    }
    
    const trimmed = addressString.trim();
    if (!trimmed) {
      return { street: '', number: '', floor: '', apartment: '' };
    }
    
    // Helper function to check if a string is numeric (including numbers with slashes like "123/5")
    const isNumeric = (str) => {
      if (!str) return false;
      // Check if it's a pure number or contains numbers with slashes/dashes
      return /^[\d\/\-]+$/.test(str);
    };
    
    // Helper function to check if a string looks like an apartment identifier
    // (short text like "St.", "A", "B", "1A", etc.)
    const looksLikeApartment = (str) => {
      if (!str) return false;
      // Short text (1-3 chars) that's not purely numeric, or alphanumeric like "1A", "2B"
      return (str.length <= 3 && !isNumeric(str)) || /^[\d]+[A-Za-z]$/.test(str);
    };
    
    // Helper function to extract floor number from text like "drugi sprat", "prvi sprat", "sprat 2", etc.
    const extractFloor = (text) => {
      if (!text) return null;
      const lower = text.toLowerCase();
      
      // Map Serbian ordinal numbers to digits
      const floorMap = {
        'prvi': '1', 'prvog': '1', 'prvom': '1',
        'drugi': '2', 'drugog': '2', 'drugom': '2',
        'treći': '3', 'trećeg': '3', 'trećem': '3', 'treci': '3', 'treceg': '3', 'trecem': '3',
        'četvrti': '4', 'četvrtog': '4', 'četvrtom': '4', 'cetvrti': '4', 'cetvrtog': '4', 'cetvrtom': '4',
        'peti': '5', 'petog': '5', 'petom': '5',
        'šesti': '6', 'šestog': '6', 'šestom': '6', 'sesti': '6', 'sestog': '6', 'sestom': '6',
        'sedmi': '7', 'sedmog': '7', 'sedmom': '7',
        'osmi': '8', 'osmog': '8', 'osmom': '8',
        'deveti': '9', 'devetog': '9', 'devetom': '9',
        'deseti': '10', 'desetog': '10', 'desetom': '10'
      };
      
      // Check for "sprat X" pattern
      const spratMatch = text.match(/sprat\s*(\d+)/i);
      if (spratMatch) {
        return spratMatch[1];
      }
      
      // Check for ordinal numbers
      for (const [word, num] of Object.entries(floorMap)) {
        if (lower.includes(word)) {
          return num;
        }
      }
      
      // Check if there's a number after "sprat"
      const spratIndex = lower.indexOf('sprat');
      if (spratIndex !== -1) {
        const afterSprat = text.substring(spratIndex + 5).trim();
        const numMatch = afterSprat.match(/^(\d+)/);
        if (numMatch) {
          return numMatch[1];
        }
      }
      
      return null;
    };
    
    // Helper function to extract apartment number from text like "stan 17", "stan 5", etc.
    const extractApartment = (text) => {
      if (!text) return null;
      const lower = text.toLowerCase();
      
      // Check for "stan X" pattern
      const stanMatch = text.match(/stan\s*(\d+)/i);
      if (stanMatch) {
        return stanMatch[1];
      }
      
      // Check if there's a number after "stan"
      const stanIndex = lower.indexOf('stan');
      if (stanIndex !== -1) {
        const afterStan = text.substring(stanIndex + 4).trim();
        const numMatch = afterStan.match(/^(\d+)/);
        if (numMatch) {
          return numMatch[1];
        }
      }
      
      return null;
    };
    
    // First, try to extract floor and apartment using text patterns
    let floor = '';
    let apartment = '';
    let workingText = trimmed;
    
    // Extract floor
    const floorNum = extractFloor(trimmed);
    if (floorNum) {
      floor = floorNum;
      // Remove floor-related text
      workingText = workingText.replace(/(drugi|prvi|treći|treci|četvrti|cetvrti|peti|šesti|sesti|sedmi|osmi|deveti|deseti)\s*sprat/gi, '');
      workingText = workingText.replace(/sprat\s*\d+/gi, '');
      workingText = workingText.replace(/sprat/gi, '');
    }
    
    // Extract apartment
    const apartmentNum = extractApartment(workingText);
    if (apartmentNum) {
      apartment = apartmentNum;
      // Remove apartment-related text
      workingText = workingText.replace(/stan\s*\d+/gi, '');
    }
    
    // Now parse the remaining address (street and number)
    const parts = workingText.split(/\s+/).filter(p => p.length > 0);
    
    if (parts.length === 0) {
      return { street: '', number: '', floor, apartment };
    }
    
    // If only one part, it's the street
    if (parts.length === 1) {
      return { street: parts[0], number: '', floor, apartment };
    }
    
    // Work backwards from the end to identify number
    let number = '';
    let streetEndIndex = parts.length;
    
    const lastPart = parts[parts.length - 1];
    if (isNumeric(lastPart)) {
      number = lastPart;
      streetEndIndex = parts.length - 1;
    }
    
    // Everything before streetEndIndex is the street
    const street = parts.slice(0, streetEndIndex).join(' ');
    
    return {
      street: street,
      number: number,
      floor: floor,
      apartment: apartment
    };
  };

  // Parse datum/vreme završetka iz job.report.completedAt (za arhivirane poslove)
  const getCompletedDateTimeFromJob = (job) => {
    if (!job || (job.status !== 'Archived' && job.status !== 'Finished')) return null;
    const d = new Date(job.report?.completedAt || job.report?.submittedAt || job.serviceDate || job.updatedAt || job.createdAt);
    if (isNaN(d.getTime())) return null;
    const y = d.getFullYear(), m = d.getMonth() + 1, day = d.getDate();
    const date = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return { date, time };
  };

  // Transform initialJobData to ensure serviceDateTime and clientAddress are properly structured
  const transformedInitialData = initialJobData ? {
    ...initialJobData,
    serviceDateTime: {
      date: initialJobData.serviceDate ? new Date(initialJobData.serviceDate).toISOString().slice(0, 10) : selectedSlot?.date || '',
      time: initialJobData.scheduledTime || selectedSlot?.time || '09:00',
    },
    // Datum i vreme završetka (za arhivu) – samo za Archived/Finished
    completedDateTime: getCompletedDateTimeFromJob(initialJobData) || { date: '', time: '00:00' },
    // Ensure serviceLocation is set (default to 'OnSite' if not present)
    serviceLocation: initialJobData.serviceLocation || 'OnSite',
    // Parse clientAddress if it's a string
    clientAddress: initialJobData.clientAddress && typeof initialJobData.clientAddress === 'string'
      ? parseAddressString(initialJobData.clientAddress)
      : (initialJobData.clientAddress && typeof initialJobData.clientAddress === 'object'
          ? initialJobData.clientAddress
          : { street: '', number: '', floor: '', apartment: '' })
  } : {};

  // Debug logging for edit mode
  if (isEdit && initialJobData) {
    console.log('🔍 JobForm Edit Mode - Initial Data:', {
      serviceLocation: initialJobData.serviceLocation,
      transformedServiceLocation: transformedInitialData.serviceLocation,
      clientAddress: initialJobData.clientAddress,
      clientAddressType: typeof initialJobData.clientAddress,
      parsedAddress: transformedInitialData.clientAddress
    });
  }
  
  const [jobData, setJobData] = useState({
    clientName: '',
    clientPhone: '',
    isBusinessClient: false,
    businessClient: {
      companyName: '',
      contactPerson: '',
      pib: '',
      registrationNumber: '',
      address: ''
    },
    clientAddress: {
      street: '',
      number: '',
      floor: '',
      apartment: '',
    },
    serviceDateTime: {
      date: selectedSlot?.date || '',
      time: selectedSlot?.time || '09:00',
    },
    completedDateTime: { date: '', time: '00:00' }, // datum/vreme završetka (arhiva)
    deviceCategoryId: '',
    deviceCategoryName: '',
    deviceTypeId: '',
    serviceId: '',
    serviceName: '',
    servicePrice: '',
    serviceDurationMinutes: '',
    status: 'draft', // Default status for new jobs
    serviceLocation: 'OnSite',
    serviceArrivalMethodId: '',
    serviceArrivalMethodName: '',
    deviceImages: [],
    ...transformedInitialData,
  });
  const [workerOptions, setWorkerOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [sparePartsOptions, setSparePartsOptions] = useState([]);
  const [deviceCategories, setDeviceCategories] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [serviceArrivalMethods, setServiceArrivalMethods] = useState([]);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const hierarchyErrorShownRef = useRef(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState({
    clientName: [],
    clientPhone: [],
    clientEmail: [],
    clientAddress: []
  });
  const [showSuggestions, setShowSuggestions] = useState({
    clientName: false,
    clientPhone: false,
    clientEmail: false,
    clientAddress: false
  });
  const [showAiExtraction, setShowAiExtraction] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiJobImages, setAiJobImages] = useState([]);
  const [showAiMultiJobModal, setShowAiMultiJobModal] = useState(false);
  const [aiExtractedJobsList, setAiExtractedJobsList] = useState([]);
  const [aiBatchRowSelected, setAiBatchRowSelected] = useState([]);
  const [aiBatchSaving, setAiBatchSaving] = useState(false);
  const [aiEditingBatchIndex, setAiEditingBatchIndex] = useState(null);
  const [aiPhoneReview, setAiPhoneReview] = useState(null);
  const [aiExtractionSourcePreviews, setAiExtractionSourcePreviews] = useState([]);
  const [showVoiceInputModal, setShowVoiceInputModal] = useState(false);
  const [voiceInputText, setVoiceInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const finalTranscriptsRef = useRef([]);
  const lastFinalIndexRef = useRef(-1);
  const isRecordingRef = useRef(false); // Use ref to track recording state in callbacks
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  const [openSections, setOpenSections] = useState(() => ({
    ...SECTION_KEYS.reduce((acc, key, index) => {
      acc[key] = index === 0;
      return acc;
    }, {}),
    legalDeadlines: false
  }));
  const [showDeviceImagesModal, setShowDeviceImagesModal] = useState(false);
  const [showDeviceQrModal, setShowDeviceQrModal] = useState(false);
  const [qrScanStatus, setQrScanStatus] = useState('Približite QR kod kameri.');
  const [qrManualValue, setQrManualValue] = useState('');
  const qrVideoRef = useRef(null);
  const qrScannerStreamRef = useRef(null);
  const qrScannerIntervalRef = useRef(null);
  const deviceImagesQuickInputRef = useRef(null);
  const dispatch = useDispatch();
  
  const jobState = useSelector((state) => state.job);
  const currentJob = jobState ? jobState.currentJob : null;
  const { clients } = useSelector((state) => state.client || {});
  const { jobs } = useSelector((state) => state.job || { jobs: [] });
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { teams } = useSelector((state) => state.team || { teams: [] });
  const { partners } = useSelector((state) => state.partner || { partners: [] });
  const { items: spareParts, loading: sparePartsLoading } = useSelector((state) => state.spareParts);
  const { user } = useSelector((state) => state.auth);
  const userForPackage = useMemo(() => mergeAuthProfile(user), [user]);
  const isCompanyUser = typeof window !== 'undefined' && localStorage.getItem('userType') === 'company';
  const countryCode = userForPackage?.result?.countryCode?.toLowerCase() || 'ba';
  const phonePrefix = COUNTRY_PREFIXES[countryCode] || '387';
  const maxJobImages = getMaxJobImages(userForPackage);
  const canUseJobImages = maxJobImages > 0;
  const canUseAI = isFeatureAllowed('ai', userForPackage);
  const canUseVoiceInput = isFeatureAllowed('voiceInput', userForPackage);
  const voiceInputRestrictionMsg = getPackageRestrictionMessage('voiceInput', userForPackage);

  const buildPhoneReviewFromExtractedJob = useCallback(
    (row, sourcePreviews = []) => {
      if (!row?.phoneNeedsReview) return null;
      const primary = row.clientPhoneFromPrimaryAI || row.clientPhoneRaw || '';
      const verifier = row.clientPhoneFromVerifierAI || '';
      const ocr = row.phoneOcrReading || '';
      const cropBox = row.phoneCropBox || null;
      const imageIndex = cropBox?.imageIndex ?? 0;
      const imageSrc = sourcePreviews[imageIndex] || null;
      const issues = [];

      if (primary && verifier && aiPhonesMismatch(primary, verifier)) {
        issues.push({
          code: 'ai_digit_mismatch',
          message: `Različita AI čitanja: "${primary}" i "${verifier}".`
        });
      }
      if (ocr && primary && aiPhonesMismatch(ocr, primary)) {
        issues.push({
          code: 'ocr_mismatch',
          message: `OCR sa slike: "${ocr}" — uporedite sa isečkom.`
        });
      }
      if (issues.length === 0) {
        issues.push({
          code: 'needs_review',
          message: 'Potvrdite broj telefona pre čuvanja.'
        });
      }

      return {
        primary,
        verifier,
        ocr,
        cropBox,
        imageSrc,
        needsReview: true,
        issues
      };
    },
    []
  );

  const normalizeAiJobCropIndexes = useCallback((jobs, imageCount) => {
    if (!Array.isArray(jobs) || imageCount <= 1 || jobs.length !== imageCount) {
      return jobs;
    }
    return jobs.map((job, idx) => {
      const box = job?.phoneCropBox;
      if (!box) return job;
      return { ...job, phoneCropBox: { ...box, imageIndex: idx } };
    });
  }, []);

  const clearClientFieldsFromJobData = useCallback(
    (prev) => ({
      ...prev,
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      clientAddress: { street: '', number: '', floor: '', apartment: '' },
      deviceType: '',
      deviceBrand: '',
      deviceModel: '',
      deviceSerialNumber: '',
      issueDescription: '',
      priority: 'medium',
      serviceDateTime: {
        date: selectedSlot?.date || '',
        time: selectedSlot?.time || '09:00'
      }
    }),
    [selectedSlot]
  );

  const applyExtractionToJobData = useCallback(
    (prev, extractedData) => {
      if (!extractedData) return prev;
      const updatedJobData = {
        ...prev,
        clientName: extractedData.clientName || prev.clientName || '',
        clientPhone: extractedData.clientPhone || prev.clientPhone || '',
        clientEmail: extractedData.clientEmail || prev.clientEmail || '',
        deviceType: extractedData.deviceType || prev.deviceType || '',
        deviceBrand: extractedData.deviceBrand || prev.deviceBrand || '',
        deviceModel: extractedData.deviceModel || prev.deviceModel || '',
        deviceSerialNumber: extractedData.deviceSerialNumber || prev.deviceSerialNumber || '',
        issueDescription: extractedData.issueDescription || prev.issueDescription || '',
        priority: extractedData.priority || prev.priority || 'medium'
      };

      if (extractedData.clientAddress) {
        if (typeof extractedData.clientAddress === 'object' && extractedData.clientAddress !== null) {
          updatedJobData.clientAddress = {
            street: extractedData.clientAddress.street || '',
            number: extractedData.clientAddress.number || '',
            floor: extractedData.clientAddress.floor || '',
            apartment: extractedData.clientAddress.apartment || ''
          };
        } else if (typeof extractedData.clientAddress === 'string') {
          updatedJobData.clientAddress = parseAddressString(extractedData.clientAddress);
        } else {
          updatedJobData.clientAddress = prev.clientAddress || {
            street: '',
            number: '',
            floor: '',
            apartment: ''
          };
        }
      }

      const primaryRaw =
        extractedData.clientPhoneFromPrimaryAI || extractedData.clientPhoneRaw || extractedData.clientPhone || '';
      const verifierRaw = extractedData.clientPhoneFromVerifierAI || '';

      if (extractedData.clientPhone && !extractedData.phoneNeedsReview) {
        updatedJobData.clientPhone =
          normalizePhoneForSave(String(extractedData.clientPhone), countryCode) || prev.clientPhone || '';
      } else if (extractedData.phoneOcrReading && extractedData.phoneNeedsReview) {
        updatedJobData.clientPhone =
          normalizePhoneForSave(String(extractedData.phoneOcrReading), countryCode) ||
          String(extractedData.phoneOcrReading);
      } else if (primaryRaw) {
        // Neslaganje ili niska pouzdanost — prikaži primarni AI kao predlog, korisnik mora proveriti
        updatedJobData.clientPhone =
          normalizePhoneForSave(String(primaryRaw), countryCode) || String(primaryRaw);
      } else if (verifierRaw) {
        updatedJobData.clientPhone =
          normalizePhoneForSave(String(verifierRaw), countryCode) || String(verifierRaw);
      }

      if (extractedData.serviceDate && String(extractedData.serviceDate).trim()) {
        const t =
          (extractedData.serviceTime && String(extractedData.serviceTime).trim()) ||
          (prev.serviceDateTime && prev.serviceDateTime.time) ||
          '';
        updatedJobData.serviceDateTime = {
          ...(prev.serviceDateTime || {}),
          date: String(extractedData.serviceDate).trim(),
          // Kalendar (vremenski slotovi) zahteva i sat; bez toga posao nije u mreži termina
          time: t || '09:00'
        };
      }

      return updatedJobData;
    },
    [countryCode]
  );

  const buildJobDataFromExtractedRow = useCallback(
    (row, basePrev) => applyExtractionToJobData(clearClientFieldsFromJobData(basePrev), row),
    [applyExtractionToJobData, clearClientFieldsFromJobData]
  );

  const buildCreatePayloadFromJobDataForBatch = useCallback(
    (jd) => {
      const addressStringRaw =
        jd.clientAddress && typeof jd.clientAddress === 'object'
          ? `${jd.clientAddress.street || ''} ${jd.clientAddress.number || ''} ${jd.clientAddress.floor || ''} ${jd.clientAddress.apartment || ''}`
          : jd.clientAddress || '';
      const addressString = addressStringRaw.replace(/\s+/g, ' ').trim();

      let sparePartsArray = [];
      if (Array.isArray(jd.usedSpareParts)) {
        sparePartsArray = jd.usedSpareParts
          .map((part) => {
            if (typeof part === 'string') {
              return part;
            }
            if (part && part.value) {
              return part.value;
            }
            if (part && part._id) {
              return part._id;
            }
            return '';
          })
          .filter((id) => id !== '');
      }

      let serviceDateValue = null;
      let scheduledTimeValue = null;
      const dateCleared = !jd.serviceDateTime?.date || String(jd.serviceDateTime.date).trim() === '';
      if (!dateCleared && jd.serviceDateTime?.date) {
        const parts = jd.serviceDateTime.date.split('-');
        if (parts.length === 3) {
          const [year, month, day] = parts;
          const d = new Date(year, month - 1, day, 12, 0, 0);
          if (!Number.isNaN(d.getTime())) {
            serviceDateValue = d;
          }
        }
        const rawT = jd.serviceDateTime.time;
        scheduledTimeValue =
          rawT != null && String(rawT).trim() !== '' ? String(rawT).trim().substring(0, 5) : null;
      } else if (jd.serviceDate) {
        const d = new Date(jd.serviceDate);
        if (!Number.isNaN(d.getTime())) {
          serviceDateValue = d;
        }
        scheduledTimeValue = jd.scheduledTime || null;
      }
      if (serviceDateValue && (!scheduledTimeValue || String(scheduledTimeValue).trim() === '')) {
        scheduledTimeValue = '09:00';
      }

      const effectiveBusinessType = jd.businessType || businessType;
      const payload = {
        status: 'In Pending',
        businessType: effectiveBusinessType,
        creator: user?.result?.email || user?.result?.name || t('common.unknown')
      };

      if (jd.serviceLocation) {
        payload.serviceLocation = jd.serviceLocation;
      }
      if (jd.serviceLocation === 'InWorkshop') {
        if (jd.serviceArrivalMethodId) {
          payload.serviceArrivalMethodId = jd.serviceArrivalMethodId;
        }
        if (jd.serviceArrivalMethodName && jd.serviceArrivalMethodName.trim()) {
          payload.serviceArrivalMethodName = jd.serviceArrivalMethodName.trim();
        }
      }

      const trimmedClientName = jd.clientName ? jd.clientName.trim() : '';
      const trimmedClientPhone = jd.clientPhone ? jd.clientPhone.toString().trim() : '';
      const trimmedClientEmail = jd.clientEmail ? jd.clientEmail.trim() : '';

      if (trimmedClientName) payload.clientName = trimmedClientName;
      if (trimmedClientPhone) payload.clientPhone = trimmedClientPhone;
      if (trimmedClientEmail) payload.clientEmail = trimmedClientEmail;
      if (addressString) payload.clientAddress = addressString;
      payload.isBusinessClient = jd.isBusinessClient === true;
      if (jd.isBusinessClient && jd.businessClient && typeof jd.businessClient === 'object') {
        payload.businessClient = {
          companyName: (jd.businessClient.companyName || '').trim(),
          contactPerson: (jd.businessClient.contactPerson || '').trim(),
          pib: (jd.businessClient.pib || '').trim(),
          registrationNumber: (jd.businessClient.registrationNumber || '').trim(),
          address: (jd.businessClient.address || '').trim()
        };
      } else {
        payload.businessClient = {
          companyName: '',
          contactPerson: '',
          pib: '',
          registrationNumber: '',
          address: ''
        };
      }

      if (jd.deviceCategoryId) {
        payload.deviceCategoryId = jd.deviceCategoryId;
      }
      if (jd.deviceCategoryName && jd.deviceCategoryName.trim()) {
        payload.deviceCategoryName = jd.deviceCategoryName.trim();
      }
      if (jd.deviceTypeId) {
        payload.deviceTypeId = jd.deviceTypeId;
      }

      const resolvedDeviceType = typeof jd.deviceType === 'string' ? jd.deviceType.trim() : '';
      if (resolvedDeviceType) {
        payload.deviceType = resolvedDeviceType;
      }

      if (jd.deviceBrand && jd.deviceBrand.trim() && jd.deviceBrand.trim() !== 'Unknown Brand') {
        payload.deviceBrand = jd.deviceBrand.trim();
      }
      if (jd.deviceModel && jd.deviceModel.trim() && jd.deviceModel.trim() !== 'Unknown Model') {
        payload.deviceModel = jd.deviceModel.trim();
      }
      if (jd.deviceSerialNumber && jd.deviceSerialNumber.trim()) {
        payload.deviceSerialNumber = jd.deviceSerialNumber.trim();
      }

      if (jd.serviceId) {
        payload.serviceId = jd.serviceId;
      }
      if (jd.serviceName && jd.serviceName.trim()) {
        payload.serviceName = jd.serviceName.trim();
      }

      if (jd.servicePrice !== '' && jd.servicePrice !== null && jd.servicePrice !== undefined) {
        const numericPrice = Number(jd.servicePrice);
        if (Number.isFinite(numericPrice)) {
          payload.servicePrice = numericPrice;
        }
      }

      if (
        jd.serviceDurationMinutes !== undefined &&
        jd.serviceDurationMinutes !== null &&
        jd.serviceDurationMinutes !== ''
      ) {
        const durationMinutes = Number(jd.serviceDurationMinutes);
        if (Number.isFinite(durationMinutes)) {
          payload.serviceDurationMinutes = durationMinutes;
        }
      }

      if (Array.isArray(jd.deviceImages) && jd.deviceImages.length > 0 && canUseJobImages) {
        payload.deviceImages = jd.deviceImages.slice(0, maxJobImages);
      }

      if (jd.issueDescription && jd.issueDescription.trim()) {
        payload.issueDescription = jd.issueDescription.trim();
      }

      if (jd.priority && String(jd.priority).trim()) {
        const p = String(jd.priority).trim();
        payload.priority = p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
      }

      if (jd.warranty === true || jd.warranty === 'yes') {
        payload.hasWarranty = true;
        if (jd.warrantyPurchaseDate) {
          payload.warrantyPurchaseDate = new Date(jd.warrantyPurchaseDate);
        }
      } else {
        payload.hasWarranty = false;
        payload.warrantyPurchaseDate = null;
      }

      if (
        jd.estimatedDuration !== undefined &&
        jd.estimatedDuration !== null &&
        jd.estimatedDuration !== ''
      ) {
        const estimatedDurationNumeric = parseFloat(jd.estimatedDuration);
        if (Number.isFinite(estimatedDurationNumeric)) {
          payload.estimatedDuration = estimatedDurationNumeric;
        }
      }

      if (jd.notes && jd.notes.trim()) {
        payload.notes = jd.notes.trim();
      }

      const assignedToValue =
        jd.assignedTo && String(jd.assignedTo).trim() && jd.assignedTo !== 'Unassigned'
          ? jd.assignedTo
          : 'Unassigned';
      payload.assignedTo = assignedToValue;

      if (sparePartsArray.length > 0) {
        payload.usedSpareParts = sparePartsArray;
      }

      if (serviceDateValue) {
        payload.serviceDate = serviceDateValue;
        if (scheduledTimeValue) {
          payload.scheduledTime = scheduledTimeValue;
        }
      }

      return payload;
    },
    [businessType, user, t, canUseJobImages, maxJobImages]
  );

  useEffect(() => {
    if (showAiMultiJobModal && aiExtractedJobsList.length > 0) {
      setAiBatchRowSelected(aiExtractedJobsList.map(() => true));
    }
  }, [showAiMultiJobModal, aiExtractedJobsList]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize Speech Recognition when modal opens
  useEffect(() => {
    if (!showVoiceInputModal) return;

    if (typeof window === 'undefined') return;

    // Check if browser supports Speech Recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      toast.error('Vaš pretraživač ne podržava prepoznavanje govora. Molimo koristite Chrome, Edge ili Safari.');
      return;
    }

    // Request microphone permission first
    const requestMicrophonePermission = async () => {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          console.log('✅ Microphone permission granted');
        }
      } catch (error) {
        console.error('❌ Microphone permission denied:', error);
        toast.error('Dozvola za mikrofon je odbijena. Molimo dozvolite pristup mikrofonu u postavkama pretraživača.');
        setShowVoiceInputModal(false);
        return;
      }
    };

    const initializeRecognition = async () => {
      // Request permission first
      await requestMicrophonePermission();

      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      // Use Croatian (hr-HR) for Serbian Latin script as sr-Latn-RS may not be supported
      recognitionInstance.lang = 'hr-HR'; // Croatian (similar to Serbian, uses Latin script)

      // Reset refs when initializing
      finalTranscriptsRef.current = [];
      lastFinalIndexRef.current = -1;

      recognitionInstance.onresult = (event) => {
        console.log('🎤 Speech recognition result event:', {
          resultsLength: event.results.length,
          isRecording: isRecordingRef.current,
          currentTranscripts: finalTranscriptsRef.current.length,
          lastIndex: lastFinalIndexRef.current
        });
        
        let interimTranscript = '';

        // Process all results, but only add new final ones
        for (let i = 0; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          const isFinal = event.results[i].isFinal;
          
          console.log(`🎤 Result ${i}:`, {
            transcript,
            isFinal,
            confidence: event.results[i][0].confidence
          });
          
          if (isFinal) {
            // This is a final result - only add if it's new
            if (i > lastFinalIndexRef.current) {
              finalTranscriptsRef.current.push(transcript.trim());
              lastFinalIndexRef.current = i;
              console.log('✅ Added final transcript:', transcript.trim());
              console.log('📊 Updated refs:', {
                transcriptsCount: finalTranscriptsRef.current.length,
                lastIndex: lastFinalIndexRef.current
              });
            }
          } else {
            // This is an interim result - only show the latest interim
            if (i === event.results.length - 1) {
              interimTranscript = transcript;
              console.log('📝 Interim transcript:', transcript);
            }
          }
        }

        // Combine all final transcripts and add current interim
        const allFinalText = finalTranscriptsRef.current.join(' ');
        const displayText = allFinalText + (interimTranscript ? ' ' + interimTranscript : '');
        
        console.log('🎤 Setting voice input text:', {
          allFinalText,
          interimTranscript,
          displayText: displayText.trim(),
          finalTranscriptsCount: finalTranscriptsRef.current.length,
          finalTranscripts: finalTranscriptsRef.current
        });
        
        setVoiceInputText(displayText.trim());
      };

      recognitionInstance.onerror = (event) => {
        console.error('❌ Speech recognition error:', {
          error: event.error,
          isRecordingRef: isRecordingRef.current,
          isRecordingState: isRecording
        });
        
        if (event.error === 'no-speech') {
          // No speech detected, but keep recording - don't stop
          console.log('⚠️ No speech detected, but continuing recording...');
          return;
        }
        if (event.error === 'not-allowed') {
          toast.error('Dozvola za mikrofon je odbijena. Molimo dozvolite pristup mikrofonu.');
          console.log('❌ Setting isRecordingRef to false due to not-allowed error');
          setIsRecording(false);
          isRecordingRef.current = false;
          return;
        }
        if (event.error === 'aborted') {
          // Recognition was stopped, don't show error
          console.log('⚠️ Recognition aborted (probably stopped manually)');
          return;
        }
        // For other errors, stop recording
        console.log('❌ Setting isRecordingRef to false due to error:', event.error);
        setIsRecording(false);
        isRecordingRef.current = false;
        toast.error(`Greška pri prepoznavanju govora: ${event.error}`);
      };

      recognitionInstance.onend = () => {
        console.log('🎤 Recognition ended');
        console.log('📊 Current state:', {
          isRecordingState: isRecording,
          isRecordingRef: isRecordingRef.current,
          transcriptsCount: finalTranscriptsRef.current.length
        });
        
        // Use ref instead of state because state might not be updated yet
        // IMPORTANT: Check ref value at the moment onend is called
        const shouldContinue = isRecordingRef.current;
        console.log('🔍 Should continue recording?', shouldContinue);
        
        if (shouldContinue) {
          // If still recording, restart recognition
          console.log('🔄 Still recording, restarting recognition...');
          try {
            // Add small delay before restart to avoid immediate restart loop
            setTimeout(() => {
              // Double-check ref value after delay
              if (isRecordingRef.current) {
                try {
                  console.log('🔄 Actually restarting recognition now...');
                  recognitionInstance.start();
                  console.log('✅ Recognition restarted');
                } catch (restartError) {
                  console.error('❌ Error restarting recognition:', restartError);
                  setIsRecording(false);
                  isRecordingRef.current = false;
                }
              } else {
                console.log('⚠️ isRecordingRef became false during delay, not restarting');
              }
            }, 200); // Increased delay to 200ms
          } catch (error) {
            console.error('❌ Error in restart logic:', error);
            setIsRecording(false);
            isRecordingRef.current = false;
          }
        } else {
          // Recognition ended and we're not recording anymore - reset transcripts
          console.log('🔄 Recognition ended and recording is stopped - resetting transcripts');
          finalTranscriptsRef.current = [];
          lastFinalIndexRef.current = -1;
        }
      };

      recognitionInstance.onstart = () => {
        console.log('✅ Speech recognition started - onstart event fired');
        console.log('🎤 Recognition instance:', {
          continuous: recognitionInstance.continuous,
          interimResults: recognitionInstance.interimResults,
          lang: recognitionInstance.lang,
          isRecording: isRecordingRef.current
        });
        // Ensure recording state is set when recognition actually starts
        if (!isRecordingRef.current) {
          console.log('⚠️ isRecordingRef was false when recognition started, setting to true');
          isRecordingRef.current = true;
          setIsRecording(true);
        }
        console.log('✅ Recording state confirmed:', {
          isRecordingRef: isRecordingRef.current,
          state: isRecording
        });
      };

      recognitionInstance.onaudiostart = () => {
        console.log('🎤 Audio capture started');
      };

      recognitionInstance.onaudioend = () => {
        console.log('🎤 Audio capture ended');
      };

      recognitionInstance.onsoundstart = () => {
        console.log('🎤 Sound detected');
      };

      recognitionInstance.onsoundend = () => {
        console.log('🎤 Sound ended');
      };

      recognitionInstance.onspeechstart = () => {
        console.log('🎤 Speech detected');
      };

      recognitionInstance.onspeechend = () => {
        console.log('🎤 Speech ended');
      };

      setRecognition(recognitionInstance);
      console.log('✅ Recognition instance created and set');
    };

    initializeRecognition().catch(error => {
      console.error('Error initializing recognition:', error);
      toast.error('Greška pri inicijalizaciji prepoznavanja govora');
    });

    return () => {
      // Cleanup will be handled by the recognition state
    };
  }, [showVoiceInputModal]);

  // Ensure serviceLocation is set to 'OnSite' if not present (important for address field visibility)
  useEffect(() => {
    if (!jobData.serviceLocation) {
      console.log('⚠️ JobForm: serviceLocation is missing, setting to OnSite');
      setJobData(prev => ({
        ...prev,
        serviceLocation: 'OnSite'
      }));
    } else {
      console.log('✅ JobForm: serviceLocation is set to:', jobData.serviceLocation);
    }
  }, [jobData.serviceLocation]);

  // Parse clientAddress if it's a string (shouldn't happen after transformation, but handle it just in case)
  useEffect(() => {
    if (jobData.clientAddress && typeof jobData.clientAddress === 'string' && jobData.clientAddress.trim()) {
      console.log('⚠️ JobForm: clientAddress is still a string, parsing it...', jobData.clientAddress);
      const parsed = parseAddressString(jobData.clientAddress);
      console.log('✅ JobForm: Parsed address:', parsed);
      setJobData(prev => ({
        ...prev,
        clientAddress: parsed
      }));
    }
  }, [jobData.clientAddress]);

  const toggleSection = useCallback((key) => {
    setOpenSections((prev) => {
      // If clicking the same section, close it. Otherwise, close all and open the clicked one.
      if (prev[key]) {
        return ACCORDION_KEYS.reduce((acc, sectionKey) => {
          acc[sectionKey] = false;
          return acc;
        }, {});
      }
      return ACCORDION_KEYS.reduce((acc, sectionKey) => {
        acc[sectionKey] = sectionKey === key;
        return acc;
      }, {});
    });
  }, []);
  
  // Add title display
  const formTitle = isEdit ? t('jobs.editJob') : 
    (initialJobData?.displayDateTime ? 
      `${t('jobs.newJob')} - ${initialJobData.displayDateTime}` : 
      t('jobs.newJob'));
  
  // Format clients for react-select
  const formattedClientOptions = clients ? clients.map(client => ({
    value: client._id,
    label: `${client.name} - ${client.phone}${client.email ? ` (${client.email})` : ''}`,
    ...client
  })) : [];

  const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if (value.id) return value.id.toString();
      if (value._id) return value._id.toString();
    }
    return value.toString();
  };

  const deviceTypeOptions = useMemo(() => {
    return deviceCategories.map((category) => {
      const categoryId = normalizeId(category.id || category._id);
      return {
        value: categoryId,
        label: category.name,
        categoryId,
        categoryName: category.name,
      };
    }).filter((option) => Boolean(option.value));
  }, [deviceCategories]);

  const selectedDeviceCategory = useMemo(() => {
    const categoryIdCandidate = jobData.deviceCategoryId || jobData.deviceTypeId;
    if (!categoryIdCandidate) return null;
    const normalized = normalizeId(categoryIdCandidate);
    return deviceCategories.find((category) => normalizeId(category.id || category._id) === normalized) || null;
  }, [deviceCategories, jobData.deviceCategoryId, jobData.deviceTypeId]);

  const servicesForSelectedCategory = useMemo(() => {
    if (!serviceTemplates || serviceTemplates.length === 0) return [];
    const selectedCategoryId = normalizeId(jobData.deviceCategoryId || jobData.deviceTypeId);
    if (!selectedCategoryId) return serviceTemplates;
    return serviceTemplates.filter(
      (service) => normalizeId(service.deviceCategoryId || service.deviceCategory?.id) === selectedCategoryId
    );
  }, [serviceTemplates, jobData.deviceCategoryId, jobData.deviceTypeId]);

  const serviceOptions = useMemo(() => {
    return servicesForSelectedCategory.map((service) => ({
      value: normalizeId(service.id || service._id),
      label: service.name,
      deviceCategoryId: normalizeId(service.deviceCategoryId || service.deviceCategory?.id),
      price: service.price ?? '',
      durationMinutes: service.durationMinutes ?? null,
      name: service.name,
    }));
  }, [servicesForSelectedCategory]);

  const arrivalMethodOptions = useMemo(() => {
    return serviceArrivalMethods.map((item) => ({
      value: normalizeId(item.id || item._id),
      label: item.name || '',
    })).filter((opt) => Boolean(opt.value));
  }, [serviceArrivalMethods]);

  const resolvedFormConfig = useMemo(() => {
    const config = { ...baseFormConfig };
    
    // Add config for field parts
    config.clientAddress_street = { ...baseFormConfig.clientAddress, label: t('jobs.streetAddress') };
    config.clientAddress_parts = { ...baseFormConfig.clientAddress, label: '' };
    config.isBusinessClient = baseFormConfig.isBusinessClient || { label: t('jobs.isBusinessClient'), type: 'checkbox', required: false };
    config.businessClient_companyName = baseFormConfig.businessClient_companyName || { label: t('jobs.businessCompanyName'), type: 'text', required: false };
    config.businessClient_contactPerson = baseFormConfig.businessClient_contactPerson || { label: t('jobs.businessContactPerson'), type: 'text', required: false };
    config.businessClient_pib = baseFormConfig.businessClient_pib || { label: t('jobs.businessPib'), type: 'text', required: false };
    config.businessClient_registrationNumber = baseFormConfig.businessClient_registrationNumber || { label: t('jobs.businessRegistrationNumber'), type: 'text', required: false };
    config.businessClient_address = baseFormConfig.businessClient_address || { label: t('jobs.businessAddress'), type: 'text', required: false };
    config.serviceDateTime_date = { ...baseFormConfig.serviceDateTime, label: t('jobs.serviceDate') };
    config.serviceDateTime_time = { ...baseFormConfig.serviceDateTime, label: t('jobs.serviceTime') };
    config.assignedTo_worker = { ...baseFormConfig.assignedTo, label: t('jobs.selectWorker') };
    config.assignedTo_team = { ...baseFormConfig.assignedTo, label: t('jobs.selectTeam') };
    config.assignedTo_partner = { ...baseFormConfig.assignedTo, label: t('jobs.selectPartner') };
    if (config.deviceType) {
      config.deviceType = {
        ...config.deviceType,
        required: false,
        options: deviceTypeOptions,
      };
    }
    if (config.serviceId) {
      config.serviceId = {
        ...config.serviceId,
        required: false,
        options: serviceOptions,
      };
    }
    if (config.serviceArrivalMethod) {
      config.serviceArrivalMethod = {
        ...config.serviceArrivalMethod,
        required: false,
        options: arrivalMethodOptions,
      };
    }
    return config;
  }, [baseFormConfig, deviceTypeOptions, serviceOptions, arrivalMethodOptions, t]);

  const selectedService = useMemo(() => {
    if (!jobData.serviceId) return null;
    return serviceTemplates.find((service) => normalizeId(service.id || service._id) === normalizeId(jobData.serviceId)) || null;
  }, [serviceTemplates, jobData.serviceId]);

  const sections = useMemo(() => ({
    clientInfo: {
      title: t('jobs.clientInfo'),
      // Row 1: clientName, Row 2: clientPhone, Row 3: serviceLocation, Row 4: serviceArrivalMethod (samo InWorkshop), clientAddress_*, Row 5: clientEmail
      fields: [
        'clientName',
        'clientPhone',
        'serviceLocation',
        'serviceArrivalMethod',
        'clientAddress_street',
        'clientAddress_parts',
        'clientEmail',
        'isBusinessClient',
        'businessClient_companyName',
        'businessClient_pib',
        'businessClient_registrationNumber'
      ]
    },
    deviceInfo: {
      title: t('jobs.deviceInfo'),
      // Row 1: deviceType, Row 2: deviceBrand, Row 3: deviceModel, Row 4: deviceSerialNumber, Row 5: issueDescription
      fields: ['deviceType', 'deviceBrand', 'deviceModel', 'deviceSerialNumber', 'issueDescription', 'deviceImagesTrigger']
    },
    serviceInfo: {
      title: t('jobs.serviceInfo'),
      fields: [
        'serviceDateTime_date',
        'serviceType',
        'serviceDateTime_time',
        'assignedTo_worker',
        'assignedTo_team',
        'assignedTo_partner'
      ]
    },
    additionalInfo: {
      title: t('jobs.additionalInfo'),
      // Row 0: warranty, Row 1: estimatedDuration, Row 2: priority, Row 3: notes (textarea)
      fields: ['warranty', 'estimatedDuration', 'priority', 'notes']
    }
  }), [t]);

  const currentCompanyId = useMemo(() => {
    const r = userForPackage?.result;
    if (!r) return null;
    if (isCompanyUser) {
      const id = r._id ?? r.id;
      return id != null && String(id).trim() !== '' ? String(id).trim() : null;
    }
    const cid = r.companyId ?? r.company?._id ?? r.company?.id;
    return cid != null && String(cid).trim() !== '' ? String(cid).trim() : null;
  }, [userForPackage, isCompanyUser]);

  const lockAssignmentFields = useMemo(
    () => isEdit && isOutgoingHandoffAssignmentLocked(initialJobData, currentCompanyId),
    [isEdit, initialJobData, currentCompanyId]
  );

  const sectionFieldNames = useMemo(
    () => SECTION_KEYS.flatMap((key) => sections[key]?.fields || []),
    [sections]
  );

  // Align row heights across all sections
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const gridContainer = document.getElementById('job-form-sections-grid');
      if (!gridContainer) return;

      // Find all field wrappers across all sections
      const allFieldWrappers = gridContainer.querySelectorAll('[data-field-row]');
      if (allFieldWrappers.length === 0) return;

      // Group fields by their row index across all sections
      const rows = new Map();
      allFieldWrappers.forEach((wrapper) => {
        const rowIndex = parseInt(wrapper.getAttribute('data-field-row'));
        if (!rows.has(rowIndex)) {
          rows.set(rowIndex, []);
        }
        rows.get(rowIndex).push(wrapper);
      });

      // Calculate max height for each row across all sections and apply it
      rows.forEach((wrappers) => {
        let maxHeight = 0;
        wrappers.forEach((wrapper) => {
          const height = wrapper.offsetHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        });
        
        // Limit max height to prevent excessive spacing (max 50px)
        const limitedHeight = Math.min(maxHeight, 50);
        
        // Apply max height to all wrappers in this row across all sections
        wrappers.forEach((wrapper) => {
          wrapper.style.minHeight = `${limitedHeight}px`;
        });
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [sections, jobData, jobData.serviceLocation]);
  
  useEffect(() => {
    if (jobData.deviceCategoryId || !jobData.deviceType || deviceCategories.length === 0) {
      return;
    }
    const match = deviceCategories.find(
      (category) => category.name?.toLowerCase() === jobData.deviceType.toLowerCase()
    );
    if (!match) return;
    const matchId = normalizeId(match.id || match._id);

    setJobData((prev) => {
      if (normalizeId(prev.deviceCategoryId) === matchId) {
        return prev;
      }
      return {
        ...prev,
        deviceCategoryId: matchId,
        deviceCategoryName: match.name || prev.deviceCategoryName || '',
        deviceTypeId: matchId,
      };
    });
  }, [deviceCategories, jobData.deviceType, jobData.deviceCategoryId]);

  useEffect(() => {
    if (jobData.serviceId || !jobData.serviceName || serviceTemplates.length === 0) {
      return;
    }
    const match = serviceTemplates.find((service) => service.name?.toLowerCase() === jobData.serviceName.toLowerCase());
    if (!match) return;
    const matchId = normalizeId(match.id || match._id);
    setJobData((prev) => {
      if (normalizeId(prev.serviceId) === matchId) {
        return prev;
      }
      return {
        ...prev,
        serviceId: matchId,
        servicePrice: match.price ?? prev.servicePrice,
        deviceCategoryId: normalizeId(match.deviceCategoryId || match.deviceCategory?.id) || prev.deviceCategoryId,
        deviceCategoryName: match.deviceCategory?.name || prev.deviceCategoryName,
        deviceTypeId: normalizeId(match.deviceCategoryId || match.deviceCategory?.id) || prev.deviceTypeId,
      };
    });
  }, [serviceTemplates, jobData.serviceName, jobData.serviceId]);
  
  useEffect(() => {
    // Fetch workers and clients when component mounts
    dispatch(getWorkers());
    dispatch(getTeams());
    dispatch(getPartners());
    dispatch(fetchClientsAsync());
  }, [dispatch]);

  // Update serviceDateTime when selectedSlot changes
  useEffect(() => {
    if (selectedSlot && !isEdit) {
      setJobData((prev) => ({
        ...prev,
        serviceDateTime: {
          date: selectedSlot.formattedDate || '',
          time: selectedSlot.formattedTime || '09:00',
        },
      }));
    }
  }, [selectedSlot, isEdit]);

  useEffect(() => {
    let isMounted = true;
    const loadHierarchy = async () => {
      if (!user?.token) {
        return;
      }
      setHierarchyLoading(true);
      hierarchyErrorShownRef.current = false;
      try {
        const [categoriesResult, servicesResult, arrivalResult] = await Promise.allSettled([
          fetchDeviceCategories(),
          fetchServicesList(),
          fetchServiceArrivalMethods()
        ]);

        if (!isMounted) return;

        if (categoriesResult.status === 'fulfilled') {
          setDeviceCategories(Array.isArray(categoriesResult.value?.data) ? categoriesResult.value.data : []);
        } else {
          console.error('Greška pri učitavanju kategorija uređaja:', categoriesResult.reason);
          setDeviceCategories([]);
          if (!hierarchyErrorShownRef.current) {
            toast.error('Ne možemo da učitamo kategorije uređaja. Proveri vezu sa serverom.');
            hierarchyErrorShownRef.current = true;
          }
        }

        if (arrivalResult.status === 'fulfilled') {
          setServiceArrivalMethods(Array.isArray(arrivalResult.value?.data) ? arrivalResult.value.data : []);
        }

        if (servicesResult.status === 'fulfilled') {
          setServiceTemplates(Array.isArray(servicesResult.value?.data) ? servicesResult.value.data : []);
        } else {
          console.error('Greška pri učitavanju usluga:', servicesResult.reason);
          setServiceTemplates([]);
          if (!hierarchyErrorShownRef.current) {
            const status = servicesResult.reason?.response?.status;
            const backendMessage = servicesResult.reason?.response?.data?.message;
            let message = 'Ne možemo da učitamo usluge. Proveri vezu sa serverom.';
            if (status === 401) {
              message = 'Sesija je istekla ili nemaš dozvolu za listu usluga.';
            } else if (status === 404) {
              message = 'Usluge još nisu podešene. Dodaj ih u sekciji "Usluge".';
            } else if (backendMessage) {
              message += ` (${backendMessage})`;
            }
            toast.error(message);
            hierarchyErrorShownRef.current = true;
          }
        }
      } catch (error) {
        console.error('Greška pri učitavanju liste usluga:', error);
        setDeviceCategories([]);
        setServiceTemplates([]);
        if (!hierarchyErrorShownRef.current) {
          const status = error?.response?.status;
          const backendMessage = error?.response?.data?.message;
          let message = 'Ne možemo da učitamo hijerarhiju usluga. Proveri da li je backend pokrenut.';
          if (status === 401) {
            message = 'Sesija je istekla ili nemaš dozvolu za hijerarhiju usluga.';
          } else if (status === 404) {
            message = 'Hijerarhija usluga još nije podešena. Dodaj kategorije i usluge u sekciji "Usluge".';
          } else if (backendMessage) {
            message += ` (${backendMessage})`;
          }
          toast.error(message);
          hierarchyErrorShownRef.current = true;
        }
      } finally {
        if (isMounted) {
          setHierarchyLoading(false);
        }
      }
    };

    loadHierarchy();

    return () => {
      isMounted = false;
    };
  }, [user?.token]);
  
  // Track if form has been initialized to prevent resetting when currentJob changes after update
  const formInitializedRef = useRef(false);
  const initialJobIdRef = useRef(null);
  
  useEffect(() => {
    // Use initialJobData if provided directly, otherwise use currentJob from Redux
    const jobToEdit = initialJobData || currentJob;
    
    // Only initialize form once when it first opens, not when currentJob changes after update
    const currentJobId = jobToEdit?._id;
    const isInitialLoad = !formInitializedRef.current || initialJobIdRef.current !== currentJobId;
    
    if (isEdit && jobToEdit && isInitialLoad) {
      console.log('📝 [JobForm] Initializing form with job data:', currentJobId);
      
      // Format the dates for the input fields (YYYY-MM-DD)
      const formattedServiceDate = jobToEdit.serviceDate ? new Date(jobToEdit.serviceDate).toISOString().slice(0, 10) : '';
      const formattedServiceTime = jobToEdit.scheduledTime || '09:00';
      
      // Ensure phone number has correct prefix when editing
      const phone = jobToEdit.clientPhone?.startsWith('+') ? 
        jobToEdit.clientPhone : 
        `+${phonePrefix}${jobToEdit.clientPhone?.replace(/^\+?[0-9]+/, '') || ''}`;
      
      // Handle assignedTo - add prefix if missing (for backward compatibility)
      let assignedToValue = jobToEdit.assignedTo;
      if (assignedToValue && assignedToValue !== 'Unassigned' && !assignedToValue.includes(':')) {
        // Legacy format - try to determine if it's a worker or partner
        const isWorker = workers.some(w => String(w._id) === String(assignedToValue));
        const isTeam = teams.some(tm => String(tm._id) === String(assignedToValue));
        const isPartner = partners.some(p => String(p._id) === String(assignedToValue));
        
        if (isWorker) {
          assignedToValue = `worker:${assignedToValue}`;
        } else if (isTeam) {
          assignedToValue = `team:${assignedToValue}`;
        } else if (isPartner) {
          assignedToValue = `partner:${assignedToValue}`;
        }
        // If neither found, keep original value (will be treated as worker for backward compatibility)
      }
      
      const completedDt = getCompletedDateTimeFromJob(jobToEdit);
      setJobData({
        ...jobToEdit,
        serviceDateTime: {
          date: formattedServiceDate,
          time: formattedServiceTime
        },
        completedDateTime: completedDt || { date: '', time: '00:00' },
        clientPhone: phone,
        assignedTo: assignedToValue,
        // Map hasWarranty to warranty for the form
        warranty: jobToEdit.hasWarranty === true || jobToEdit.hasWarranty === 'yes',
        warrantyPurchaseDate: jobToEdit.warrantyPurchaseDate || '',
        notes: jobToEdit.notes || '',
        clientNotifiedAt: jobToEdit.clientNotifiedAt ? new Date(jobToEdit.clientNotifiedAt).toISOString().slice(0, 10) : ''
      });
      
      // Mark as initialized
      formInitializedRef.current = true;
      initialJobIdRef.current = currentJobId;
    } else if (isEdit && jobToEdit && !isInitialLoad) {
      console.log('📝 [JobForm] Skipping form reset - form already initialized, user is editing');
    }
    
    return () => {
      // Clean up when the component unmounts
      if (isEdit) {
        dispatch(clearCurrentJob());
        formInitializedRef.current = false;
        initialJobIdRef.current = null;
      }
    };
  }, [isEdit, initialJobData, dispatch, phonePrefix, workers, teams, partners]);
  
  // Dodela radnika/tima — onemogući nedostupne za datum servisa
  useEffect(() => {
    const serviceDate = jobData.serviceDateTime?.date || '';
    setWorkerOptions(buildWorkerAssignmentOptions(workers, serviceDate));
    setTeamOptions(buildTeamAssignmentOptions(teams, workers, serviceDate));
  }, [workers, teams, jobData.serviceDateTime?.date]);

  useEffect(() => {
    const serviceDate = jobData.serviceDateTime?.date;
    if (!serviceDate) return;
    setJobData((prev) => {
      if (!prev.assignedTo) return prev;
      if (isAssignmentAvailableOnDate(prev.assignedTo, workers, teams, serviceDate)) {
        return prev;
      }
      toast.info('Dodela je uklonjena — radnik ili tim nije dostupan za izabrani datum.');
      return { ...prev, assignedTo: '' };
    });
  }, [jobData.serviceDateTime?.date, workers, teams]);
  
  // Update partner options when partners change
  useEffect(() => {
    if (partners && partners.length > 0) {
      const activePartners = partners.filter(partner => partner.active);
      const options = activePartners.map(partner => ({
        value: String(partner._id),
        label: partner.companyName
      }));
      setPartnerOptions(options);
    } else {
      setPartnerOptions([]);
    }
  }, [partners]);
  
  // Fetch spare parts
  useEffect(() => {
    dispatch(getSpareParts());
  }, [dispatch]);
  
  useEffect(() => {
    if (spareParts && spareParts.length > 0) {
      const mappedOptions = spareParts.map(part => ({
        value: part._id,
        label: part.name, // Show only name without description
        price: part.price,
        purchasePrice: part.purchasePrice,
        tax: part.tax,
        quantity: part.quantity,
        name: part.name,
        code: part.code
      }));
      setSparePartsOptions(mappedOptions);
    }
  }, [spareParts]);
  
  useEffect(() => {
    // Initialize phone with prefix when form is reset
    if (!jobData.clientPhone) {
      setJobData(prev => ({
        ...prev,
        clientPhone: `+${phonePrefix}`
      }));
    }
  }, [phonePrefix]);
  
  // Function to filter clients based on input value and field
  const filterClients = (value, field) => {
    if (!value || !clients) return [];
    
    const searchValue = value.toLowerCase();
    return clients.filter(client => {
      switch(field) {
        case 'clientName':
          if (value.length < 2) return false;
          return client.name.toLowerCase().includes(searchValue);
        case 'clientPhone':
          // Extract digits only from the phone value (remove + and other chars)
          const inputDigits = value.replace(/\D/g, '');
          // For phone, show suggestions only if 6+ digits are entered
          if (inputDigits.length < 6) return false;
          // Extract digits from client phone
          const clientPhoneDigits = (client.phone || '').replace(/\D/g, '');
          if (!clientPhoneDigits) return false;
          // Check if client phone starts with input digits
          return clientPhoneDigits.startsWith(inputDigits);
        case 'clientEmail':
          if (value.length < 2) return false;
          return client.email?.toLowerCase().includes(searchValue);
        case 'clientAddress':
          if (value.length < 2) return false;
          const address = `${client.address?.street || ''} ${client.address?.number || ''}`.toLowerCase();
          return address.includes(searchValue);
        default:
          return false;
      }
    }).slice(0, 5); // Limit to 5 suggestions
  };

  // Handle input change with suggestions
  const handleClientFieldChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'clientPhone') {
      setAiPhoneReview(null);
      const defaultPrefix = `+${phonePrefix}`;
      
      console.log('📞 handleClientFieldChange - POČETAK:', {
        name,
        value,
        defaultPrefix,
        startsWithPlus: value?.startsWith('+')
      });
      
      // If value is empty, set to default prefix only
      if (!value || value === '') {
        console.log('📞 handleClientFieldChange - Prazna vrednost, postavljam defaultPrefix');
        setJobData(prev => ({
          ...prev,
          [name]: defaultPrefix
        }));
        return;
      }
      
      // Dozvoli bilo koji prefiks koji počinje sa '+' - nema ograničenja
      // Jedino obavezno je da počinje sa '+'
      if (value.startsWith('+')) {
        console.log('✅ handleClientFieldChange - Vrednost počinje sa +, čuvam kao:', value);
        setJobData(prev => {
          const updated = {
            ...prev,
            [name]: value
          };
          console.log('✅ handleClientFieldChange - Ažuriran jobData:', {
            oldValue: prev[name],
            newValue: value,
            updated: updated[name]
          });
          return updated;
        });
      } else {
        // Ako ne počinje sa '+', dodaj ga
        console.log('⚠️ handleClientFieldChange - Vrednost ne počinje sa +, dodajem +');
        const withPlus = '+' + value.replace(/\+/g, '');
        setJobData(prev => {
          const updated = {
            ...prev,
            [name]: withPlus
          };
          console.log('✅ handleClientFieldChange - Ažuriran jobData sa +:', {
            oldValue: prev[name],
            newValue: withPlus,
            updated: updated[name]
          });
          return updated;
        });
      }
      
      // Declare normalized for suggestions (reuse logic above)
      const prefixDigits = phonePrefix.replace(/\D/g, '');
      let normalized;
      if (value.startsWith(defaultPrefix)) {
        normalized = value;
      } else {
        let allDigits = value.replace(/\D/g, '');
        let digitsWithoutPrefix = allDigits;
        while (digitsWithoutPrefix.startsWith(prefixDigits) && digitsWithoutPrefix.length > prefixDigits.length) {
          digitsWithoutPrefix = digitsWithoutPrefix.slice(prefixDigits.length);
        }
        normalized = digitsWithoutPrefix ? `${defaultPrefix}${digitsWithoutPrefix}` : defaultPrefix;
      }
      
      // Show suggestions if 6+ digits are entered (excluding prefix)
      // Extract digits after the + sign (prefix + user input)
      const digitsAfterPlus = normalized.includes('+') 
        ? normalized.split('+').slice(1).join('').replace(/\D/g, '')
        : normalized.replace(/\D/g, '');
      
      // Count only digits after prefix (remove prefix digits)
      const userInputDigits = digitsAfterPlus.startsWith(prefixDigits)
        ? digitsAfterPlus.slice(prefixDigits.length)
        : digitsAfterPlus;
      
      if (userInputDigits.length >= 6) {
        const filtered = filterClients(normalized, 'clientPhone');
        setSuggestions(prev => ({
          ...prev,
          clientPhone: filtered
        }));
        setShowSuggestions(prev => ({
          ...prev,
          clientPhone: filtered.length > 0
        }));
        
        // Auto-fill client data if exact match is found (only universal data, not specific like model/description)
        // ALI samo ako korisnik nije aktivno brišeo deo broja (ne vraćaj vrednost na staru)
        if (filtered.length === 1) {
          const matchedClient = filtered[0];
          // Extract digits from both phones for comparison
          const normalizedDigits = normalized.replace(/\D/g, '');
          const clientPhoneDigits = (matchedClient.phone || '').replace(/\D/g, '');
          if (!clientPhoneDigits) {
            return;
          }
          
          // If phones match exactly, auto-fill universal client data
          // ALI samo ako trenutna vrednost NIJE kraća od prethodne (korisnik nije brisao)
          const currentJobDataPhone = jobData.clientPhone || '';
          const currentPhoneDigits = currentJobDataPhone.replace(/\D/g, '');
          const isDeleting = normalizedDigits.length < currentPhoneDigits.length;
          
          // Auto-fill samo ako korisnik nije brisao (dodaje nove cifre) ILI ako je tačno poklapanje
          if (!isDeleting && (normalizedDigits === clientPhoneDigits || clientPhoneDigits.startsWith(normalizedDigits))) {
            console.log('✅ Auto-fill: Popunjavam podatke klijenta (korisnik nije brisao)');
            setJobData(prev => ({
              ...prev,
              clientName: matchedClient.name || prev.clientName,
              // NE menjaj clientPhone ako korisnik aktivno briše - koristi trenutnu vrednost
              clientPhone: normalized, // Koristi trenutnu vrednost koju je korisnik uneo
              clientEmail: matchedClient.email || prev.clientEmail,
              clientAddress: matchedClient.address?.street && matchedClient.address?.number
                ? {
                    street: matchedClient.address.street || prev.clientAddress?.street || '',
                    number: matchedClient.address.number || prev.clientAddress?.number || '',
                    floor: matchedClient.address.floor || prev.clientAddress?.floor || '',
                    apartment: matchedClient.address.apartment || prev.clientAddress?.apartment || '',
                    isHouse: matchedClient.address.isHouse || prev.clientAddress?.isHouse || false
                  }
                : prev.clientAddress
              // Note: We don't auto-fill device-specific fields like deviceBrand, deviceModel, issueDescription
            }));
          } else if (isDeleting) {
            console.log('⚠️ Auto-fill: Preskačem auto-fill jer korisnik briše broj');
          }
        }
      } else {
        setShowSuggestions(prev => ({
          ...prev,
          clientPhone: false
        }));
        setSuggestions(prev => ({
          ...prev,
          clientPhone: []
        }));
      }
      return;
    }
    
    setJobData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (client, field) => {
    console.log('🎯 handleSuggestionSelect - POČETAK:', {
      field,
      clientPhone: client.phone,
      clientName: client.name
    });
    
    setJobData(prev => {
      let phoneValue = prev.clientPhone;
      
      if (field === 'clientPhone') {
        // Koristi telefon iz klijenta, ali obezbedi da počinje sa '+'
        if (client.phone && client.phone.startsWith('+')) {
          phoneValue = client.phone;
        } else if (client.phone) {
          // Ako nema '+', dodaj default prefiks
          phoneValue = `+${phonePrefix}${client.phone.replace(/^\+?[0-9]+/, '')}`;
        } else {
          // Ako nema telefon, koristi default prefiks
          phoneValue = `+${phonePrefix}`;
        }
        console.log('🎯 handleSuggestionSelect - Postavljam clientPhone na:', phoneValue);
      }
      
      const updated = {
        ...prev,
        clientName: field === 'clientName' ? client.name : prev.clientName,
        clientPhone: field === 'clientPhone' ? phoneValue : prev.clientPhone,
        clientEmail: field === 'clientEmail' ? client.email : prev.clientEmail,
        clientAddress: field === 'clientAddress' ? 
          `${client.address?.street || ''} ${client.address?.number || ''}` : 
          prev.clientAddress
      };
      
      console.log('✅ handleSuggestionSelect - jobData ažuriran:', updated);
      return updated;
    });
    
    setShowSuggestions({
      clientName: false,
      clientPhone: false,
      clientEmail: false,
      clientAddress: false
    });
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.suggestions-container')) {
        setShowSuggestions({
          clientName: false,
          clientPhone: false,
          clientEmail: false,
          clientAddress: false
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleInputChange = (field, value) => {
    if (field === 'clientAddress') {
      setJobData(prev => ({
        ...prev,
        clientAddress: {
          ...prev.clientAddress,
          ...value,
        },
      }));
    } else if (field === 'businessClient') {
      setJobData(prev => ({
        ...prev,
        businessClient: {
          ...(prev.businessClient || {}),
          ...value,
        },
      }));
    } else if (field === 'isBusinessClient') {
      setJobData(prev => ({
        ...prev,
        isBusinessClient: value === true,
        businessClient: value === true
          ? (prev.businessClient || {
              companyName: '',
              contactPerson: '',
              pib: '',
              registrationNumber: '',
              address: ''
            })
          : {
              companyName: '',
              contactPerson: '',
              pib: '',
              registrationNumber: '',
              address: ''
            }
      }));
    } else if (field === 'serviceDateTime') {
      console.log('📅 Updating serviceDateTime:', value, 'previous:', jobData.serviceDateTime);
      setJobData(prev => {
        const updated = {
          ...prev,
          serviceDateTime: {
            ...prev.serviceDateTime,
            ...value,
          },
        };
        console.log('📅 Updated serviceDateTime:', updated.serviceDateTime);
        return updated;
      });
    } else {
      setJobData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleDeviceTypeSelect = useCallback((categoryId) => {
    if (!categoryId) {
      setJobData(prev => ({
        ...prev,
        deviceCategoryId: '',
        deviceCategoryName: '',
        deviceTypeId: '',
        deviceType: '',
        serviceId: '',
        serviceName: '',
        servicePrice: '',
        serviceDurationMinutes: '',
      }));
      return;
    }

    const category = deviceCategories.find((item) => normalizeId(item.id || item._id) === categoryId);
    const label = category?.name || deviceTypeOptions.find(option => option.value === categoryId)?.categoryName || '';

    setJobData(prev => ({
      ...prev,
      deviceTypeId: categoryId,
      deviceType: label || prev.deviceType,
      deviceCategoryId: categoryId,
      deviceCategoryName: label || prev.deviceCategoryName,
      serviceId: '',
      serviceName: '',
      servicePrice: '',
      serviceDurationMinutes: '',
    }));
  }, [deviceCategories, deviceTypeOptions, normalizeId]);

  const handleServiceSelect = useCallback((serviceId) => {
    if (!serviceId) {
      setJobData(prev => ({
        ...prev,
        serviceId: '',
        serviceName: '',
        servicePrice: '',
        serviceDurationMinutes: null,
      }));
      return;
    }

    const serviceOption = serviceOptions.find(option => option.value === serviceId);
    const serviceEntity = serviceTemplates.find(service => normalizeId(service.id || service._id) === serviceId);
    const relatedCategoryId = serviceOption?.deviceCategoryId || normalizeId(serviceEntity?.deviceCategoryId || serviceEntity?.deviceCategory?.id);
    const relatedCategory = deviceCategories.find(category => normalizeId(category.id || category._id) === relatedCategoryId);

    setJobData(prev => ({
      ...prev,
      serviceId,
      serviceName: serviceEntity?.name || serviceOption?.label || prev.serviceName,
      servicePrice: serviceEntity?.price ?? serviceOption?.price ?? prev.servicePrice,
      deviceCategoryId: relatedCategoryId || prev.deviceCategoryId,
      deviceCategoryName: relatedCategory?.name || prev.deviceCategoryName,
      deviceTypeId: relatedCategoryId || prev.deviceTypeId,
      deviceType: relatedCategory?.name || prev.deviceType,
      serviceDurationMinutes: serviceEntity?.durationMinutes ?? serviceOption?.durationMinutes ?? prev.serviceDurationMinutes ?? null,
      estimatedDuration:
        serviceEntity?.durationMinutes !== undefined && serviceEntity?.durationMinutes !== null
          ? Number((serviceEntity.durationMinutes / 60).toFixed(2))
          : prev.estimatedDuration,
    }));
  }, [deviceCategories, serviceOptions, serviceTemplates, normalizeId]);
  
  const handleSelectChange = (selectedOption, { name }) => {
    if (!selectedOption) {
      setJobData(prev => ({ ...prev, [name]: '' }));
      return;
    }
    setJobData(prev => ({ ...prev, [name]: selectedOption.value }));
  };
  
  const handleClientSelect = (selectedOption) => {
    if (selectedOption) {
      const phone = selectedOption.phone.startsWith('+') ? 
        selectedOption.phone : 
        `+${phonePrefix}${selectedOption.phone.replace(/^\+?[0-9]+/, '')}`;
      
      setJobData({
        ...jobData,
        clientName: selectedOption.name,
        clientPhone: phone,
        clientEmail: selectedOption.email
      });
    }
  };
  
  const handleSparePartsChange = useCallback((selectedOptions) => {
    setJobData(prev => ({
      ...prev,
      usedSpareParts: selectedOptions,
    }));
  }, []);

  const handleDeviceImagesAdd = useCallback((files) => {
    if (!canUseJobImages || !files || files.length === 0) return;

    const current = jobData.deviceImages || [];
    const remainingSlots = maxJobImages - current.length;
    if (remainingSlots <= 0) {
      toast.warning(`Dostigli ste maksimalan broj slika (${maxJobImages}) za vaš paket.`);
      return;
    }

    const filesArray = Array.from(files).slice(0, remainingSlots);
    const readers = [];

    filesArray.forEach((file) => {
      const reader = new FileReader();
      readers.push(
        new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = () => resolve(null);
        })
      );
      reader.readAsDataURL(file);
    });

    Promise.all(readers).then((results) => {
      const valid = results.filter((r) => typeof r === 'string' && r.startsWith('data:image/'));
      if (valid.length > 0) {
        setJobData((prev) => ({
          ...prev,
          deviceImages: [...(prev.deviceImages || []), ...valid].slice(0, maxJobImages),
        }));
      }
    });
  }, [canUseJobImages, jobData.deviceImages, maxJobImages]);

  const handleDeviceImageRemove = useCallback((index) => {
    setJobData((prev) => {
      const next = [...(prev.deviceImages || [])];
      next.splice(index, 1);
      return { ...prev, deviceImages: next };
    });
  }, []);

  const applyDeviceQrData = useCallback((rawText) => {
    if (!rawText || typeof rawText !== 'string') return false;
    const text = rawText.trim();
    if (!text) return false;

    let parsed = null;
    try {
      parsed = JSON.parse(text);
    } catch (error) {
      parsed = null;
    }

    const pickValue = (...keys) => {
      for (const key of keys) {
        const value = parsed && typeof parsed === 'object' ? parsed[key] : null;
        if (typeof value === 'string' && value.trim()) return value.trim();
      }
      return '';
    };

    const extractByRegex = (pattern) => {
      const match = text.match(pattern);
      return match?.[1]?.trim?.() || '';
    };

    const serialNumber = pickValue('serial', 'serialNumber', 'sn') || extractByRegex(/(?:SN|SERIAL|SERIJSKI(?:\s*BROJ)?)\s*[:=]\s*([A-Z0-9\-_]+)/i);
    const model = pickValue('model', 'deviceModel') || extractByRegex(/(?:MODEL)\s*[:=]\s*([A-Z0-9\-_\.]+)/i);
    const brand = pickValue('brand', 'manufacturer', 'deviceBrand') || extractByRegex(/(?:BRAND|BREND|MANUFACTURER)\s*[:=]\s*([A-Z0-9\-_\. ]+)/i);
    const deviceType = pickValue('deviceType', 'type') || extractByRegex(/(?:TYPE|TIP)\s*[:=]\s*([A-Z0-9\-_\. ]+)/i);

    if (!serialNumber && !model && !brand && !deviceType) {
      return false;
    }

    setJobData((prev) => ({
      ...prev,
      deviceSerialNumber: serialNumber || prev.deviceSerialNumber || '',
      deviceModel: model || prev.deviceModel || '',
      deviceBrand: brand || prev.deviceBrand || '',
      deviceType: deviceType || prev.deviceType || '',
    }));

    return true;
  }, []);

  const stopQrScanner = useCallback(() => {
    if (qrScannerIntervalRef.current) {
      clearInterval(qrScannerIntervalRef.current);
      qrScannerIntervalRef.current = null;
    }
    if (qrScannerStreamRef.current) {
      qrScannerStreamRef.current.getTracks().forEach((track) => track.stop());
      qrScannerStreamRef.current = null;
    }
    if (qrVideoRef.current) {
      qrVideoRef.current.srcObject = null;
    }
  }, []);

  const startQrScanner = useCallback(async () => {
    if (!showDeviceQrModal) return;
    if (typeof window === 'undefined') return;
    if (!('BarcodeDetector' in window)) {
      setQrScanStatus('Vaš browser ne podržava automatsko QR skeniranje. Nalepite QR tekst ručno ispod.');
      return;
    }

    try {
      setQrScanStatus('Pokrećem kameru...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      qrScannerStreamRef.current = stream;

      if (!qrVideoRef.current) return;
      qrVideoRef.current.srcObject = stream;
      await qrVideoRef.current.play();

      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      setQrScanStatus('Skeniranje u toku...');

      qrScannerIntervalRef.current = window.setInterval(async () => {
        if (!qrVideoRef.current) return;
        try {
          const barcodes = await detector.detect(qrVideoRef.current);
          if (!barcodes || barcodes.length === 0) return;

          const rawValue = barcodes[0]?.rawValue || '';
          const applied = applyDeviceQrData(rawValue);
          if (applied) {
            setQrScanStatus('QR uspešno očitan. Polja su popunjena.');
            toast.success('QR aparata je uspešno pročitan.');
            stopQrScanner();
            setShowDeviceQrModal(false);
          } else {
            setQrScanStatus('QR je očitan, ali nema prepoznatih polja (model/serijski).');
          }
        } catch (error) {
          // Ignore per-frame detection errors
        }
      }, 500);
    } catch (error) {
      setQrScanStatus('Ne mogu da pristupim kameri. Dozvolite camera access ili unesite QR tekst ručno.');
    }
  }, [applyDeviceQrData, showDeviceQrModal, stopQrScanner]);

  useEffect(() => {
    if (showDeviceQrModal) {
      startQrScanner();
      return;
    }
    stopQrScanner();
  }, [showDeviceQrModal, startQrScanner, stopQrScanner]);

  useEffect(() => () => stopQrScanner(), [stopQrScanner]);

  const openDeviceImagesPicker = useCallback(() => {
    if (!canUseJobImages) return;
    if (deviceImagesQuickInputRef.current) {
      deviceImagesQuickInputRef.current.click();
    }
  }, [canUseJobImages]);

  const handleFieldChange = useCallback(
    (field, value) => {
      if (field === 'clientAddress' || field === 'serviceDateTime') {
        handleInputChange(field, value);
        return;
      }
      if (field === 'usedSpareParts') {
        handleSparePartsChange(value);
        return;
      }
      if (field === 'deviceType') {
        handleDeviceTypeSelect(value);
        return;
      }
      if (field === 'serviceId') {
        handleServiceSelect(value);
        return;
      }
      if (field === 'serviceArrivalMethod') {
        const opt = arrivalMethodOptions.find((o) => String(o.value).trim() === String(value).trim());
        setJobData((prev) => ({
          ...prev,
          serviceArrivalMethodId: value || '',
          serviceArrivalMethodName: opt?.label || '',
        }));
        return;
      }
      // Use handleClientFieldChange for clientPhone to enable suggestions
      if (field === 'clientPhone' && typeof value === 'string') {
        const syntheticEvent = {
          target: {
            name: 'clientPhone',
            value: value
          }
        };
        handleClientFieldChange(syntheticEvent);
        return;
      }
      // Handle serviceLocation change - ensure it updates jobData
      if (field === 'serviceLocation') {
        console.log('🔄 JobForm: Updating serviceLocation to:', value);
        setJobData(prev => {
          const updated = {
            ...prev,
            serviceLocation: value
          };
          console.log('🔄 JobForm: Updated jobData.serviceLocation:', updated.serviceLocation);
          return updated;
        });
        return;
      }
      handleInputChange(field, value);
    },
    [handleDeviceTypeSelect, handleInputChange, handleServiceSelect, handleSparePartsChange, handleClientFieldChange, arrivalMethodOptions]
  );

  // Novi izbor fajlova DODAJE na postojeće (ranije je svaki izbor brišao prethodne slike)
  const handleAiJobImagesPick = useCallback((fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    setAiJobImages((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const fileKey = (f) => `${f.name}-${f.size}-${f.lastModified}`;
      const seen = new Set(existing.map(fileKey));
      const merged = [...existing];
      for (const f of picked) {
        if (merged.length >= MAX_AI_JOB_IMAGES) break;
        const k = fileKey(f);
        if (seen.has(k)) continue;
        seen.add(k);
        merged.push(f);
      }
      return merged;
    });
  }, []);

  const removeAiJobImageAt = useCallback((index) => {
    setAiJobImages((prev) => (Array.isArray(prev) ? prev.filter((_, i) => i !== index) : []));
  }, []);
  
  // Tekst i/ili slike u jednom toku; slike + opcioni tekst (npr. zakazivanje) ide na isti API
  const handleAiExtraction = async () => {
    const hasMsg = aiMessage.trim().length > 0;
    const hasImg = aiJobImages && aiJobImages.length > 0;

    if (!hasMsg && !hasImg) {
      toast.error('Unesite tekst poruke ili priložite bar jednu sliku.');
      return;
    }
    if (hasImg && !canUseAI) {
      toast.error('AI sa slikama dostupan je od Starter paketa (mesečni limit slika po pretplati).');
      return;
    }

    setAiLoading(true);
    try {
      let jobs = [];
      let sourcePreviews = [];

      if (hasImg) {
        sourcePreviews = await Promise.all(
          aiJobImages.map(
            (f) =>
              new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(f);
              })
          )
        );
        setAiExtractionSourcePreviews(sourcePreviews);

        const fd = new FormData();
        aiJobImages.forEach((f) => fd.append('images', f));
        if (hasMsg) {
          fd.append('message', aiMessage.trim());
        }
        const response = await extractJobDataFromImages(fd);
        if (response.data?.success) {
          jobs = Array.isArray(response.data.jobs)
            ? response.data.jobs
            : response.data.data
              ? [response.data.data]
              : [];
          jobs = normalizeAiJobCropIndexes(jobs, sourcePreviews.length);
        }
      } else {
        const response = await extractJobDataFromMessage(aiMessage.trim());
        if (response.data?.success && response.data.data) {
          jobs = [response.data.data];
        }
      }

      if (!jobs.length) {
        toast.error('Nije moguće izvući podatke. Molimo pokušajte ponovo.');
        return;
      }

      if (jobs.length === 1) {
        setAiExtractedJobsList([]);
        setJobData((prev) => applyExtractionToJobData(prev, jobs[0]));
        setAiPhoneReview(
          jobs[0].phoneNeedsReview
            ? buildPhoneReviewFromExtractedJob(jobs[0], sourcePreviews)
            : null
        );
        setShowAiExtraction(false);
        setAiMessage('');
        setAiJobImages([]);
        if (jobs[0].phoneNeedsReview) {
          toast.warn('Proverite broj telefona na isečku sa slike pre čuvanja.', { autoClose: 6000 });
        } else {
          toast.success('Podaci su uspešno izvučeni i popunjeni u formu!');
        }
      } else {
        setAiExtractedJobsList(jobs);
        setAiEditingBatchIndex(null);
        setAiPhoneReview(null);
        setShowAiMultiJobModal(true);
        const reviewCount = jobs.filter((j) => j.phoneNeedsReview).length;
        if (reviewCount > 0) {
          toast.warn(
            `Prepoznato ${jobs.length} intervencija. ${reviewCount} red(ova) zahteva potvrdu telefona.`,
            { autoClose: 6000 }
          );
        } else {
          toast.success(`Prepoznato je ${jobs.length} intervencija. Označite redove i kreirajte.`);
        }
      }
    } catch (error) {
      console.error('Error extracting data (AI):', error);
      const errorMessage =
        error?.response?.data?.message || error.message || 'Greška pri ekstrakciji podataka';
      toast.error(errorMessage);
    } finally {
      setAiLoading(false);
    }
  };

  const handleQuickAddCategory = async (e) => {
    e.preventDefault();
    
    try {
      const name = window.prompt('Naziv kategorije uređaja:', '');
      if (!name) {
        toast.info('Dodavanje kategorije otkazano');
        return;
      }

      // Check if category already exists
      const existingCategory = deviceCategories.find(cat => 
        cat.name.toLowerCase() === name.toLowerCase()
      );

      if (existingCategory) {
        // If exists, just select it
        const categoryId = normalizeId(existingCategory.id || existingCategory._id);
        setJobData(prev => ({
          ...prev,
          deviceCategoryId: categoryId,
          deviceCategoryName: existingCategory.name,
          deviceTypeId: categoryId,
          deviceType: existingCategory.name
        }));
        toast.success(`Kategorija "${existingCategory.name}" je već izabrana`);
        return;
      }

      // Create new category
      const categoryData = {
        name: name.trim(),
        color: '#3B82F6' // Default color
      };

      const result = await createDeviceCategory(categoryData);
      const newCategory = result.data;
      
      // Reload categories
      const categoriesResult = await fetchDeviceCategories();
      if (categoriesResult?.data) {
        setDeviceCategories(Array.isArray(categoriesResult.data) ? categoriesResult.data : []);
      }
      
      // Select the newly created category
      const categoryId = normalizeId(newCategory.id || newCategory._id);
      setJobData(prev => ({
        ...prev,
        deviceCategoryId: categoryId,
        deviceCategoryName: newCategory.name,
        deviceTypeId: categoryId,
        deviceType: newCategory.name
      }));
      
      toast.success('Kategorija uspešno dodata');
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error(`Greška: ${error.response?.data?.message || error.message || 'Greška pri dodavanju kategorije'}`);
    }
  };

  const handleQuickAddService = async (e) => {
    e.preventDefault();
    
    try {
      // Check if category is selected first
      if (!jobData.deviceCategoryId) {
        toast.warning('Prvo izaberite kategoriju uređaja');
        return;
      }

      const name = window.prompt('Naziv usluge:', '');
      if (!name) {
        toast.info('Dodavanje usluge otkazano');
        return;
      }

      // Check if service already exists for this category
      const selectedCategoryId = normalizeId(jobData.deviceCategoryId);
      const existingService = serviceTemplates.find(service => 
        service.name.toLowerCase() === name.toLowerCase() &&
        normalizeId(service.deviceCategoryId || service.deviceCategory?.id) === selectedCategoryId
      );

      if (existingService) {
        // If exists, just select it
        const serviceId = normalizeId(existingService.id || existingService._id);
        setJobData(prev => ({
          ...prev,
          serviceId: serviceId,
          serviceName: existingService.name,
          servicePrice: existingService.price || '',
          serviceDurationMinutes: existingService.durationMinutes || ''
        }));
        toast.success(`Usluga "${existingService.name}" je već izabrana`);
        return;
      }

      // Get optional fields
      const price = window.prompt('Cena usluge (RSD):', '');
      const durationMinutes = window.prompt('Trajanje (minuti):', '');

      // Create new service
      const serviceData = {
        name: name.trim(),
        deviceCategoryId: jobData.deviceCategoryId,
        price: price ? parseFloat(price) : null,
        durationMinutes: durationMinutes ? parseInt(durationMinutes) : null
      };

      const result = await createServiceItem(serviceData);
      const newService = result.data;
      
      // Reload services
      const servicesResult = await fetchServicesList();
      if (servicesResult?.data) {
        setServiceTemplates(Array.isArray(servicesResult.data) ? servicesResult.data : []);
      }
      
      // Select the newly created service
      const serviceId = normalizeId(newService.id || newService._id);
      setJobData(prev => ({
        ...prev,
        serviceId: serviceId,
        serviceName: newService.name,
        servicePrice: newService.price || '',
        serviceDurationMinutes: newService.durationMinutes || ''
      }));
      
      toast.success('Usluga uspešno dodata');
    } catch (error) {
      console.error('Error adding service:', error);
      toast.error(`Greška: ${error.response?.data?.message || error.message || 'Greška pri dodavanju usluge'}`);
    }
  };

  const handleQuickAddArrivalMethod = async (e) => {
    e.preventDefault();
    try {
      const name = window.prompt(t('jobs.selectArrivalMethod') || 'Naziv načina dostave (npr. Bex, Aks, D express):', '');
      if (!name || !name.trim()) {
        toast.info('Dodavanje načina dolaska otkazano');
        return;
      }
      const trimmedName = name.trim();
      const existing = serviceArrivalMethods.find((m) => (m.name || '').toLowerCase() === trimmedName.toLowerCase());
      if (existing) {
        const id = normalizeId(existing.id || existing._id);
        setJobData((prev) => ({
          ...prev,
          serviceArrivalMethodId: id,
          serviceArrivalMethodName: existing.name || trimmedName,
        }));
        toast.success(`"${existing.name}" je već u listi i izabran`);
        return;
      }
      const result = await createServiceArrivalMethod({ name: trimmedName });
      const created = result.data;
      const listResult = await fetchServiceArrivalMethods();
      if (listResult?.data) {
        setServiceArrivalMethods(Array.isArray(listResult.data) ? listResult.data : []);
      }
      const id = normalizeId(created.id || created._id);
      setJobData((prev) => ({
        ...prev,
        serviceArrivalMethodId: id,
        serviceArrivalMethodName: created.name || trimmedName,
      }));
      toast.success(t('jobs.quickAddArrivalMethod') ? 'Način dolaska dodat' : 'Način dolaska uspešno dodat');
    } catch (err) {
      console.error('Error adding arrival method:', err);
      toast.error(err.response?.data?.message || err.message || 'Greška pri dodavanju načina dolaska');
    }
  };

  const handleQuickAddSparePart = async (e) => {
    e.preventDefault();
    
    try {
      const name = window.prompt('Naziv rezervnog dela:', '');
      if (!name) {
        toast.info('Dodavanje rezervnog dela otkazano');
        return;
      }

      // Check if part already exists in spareParts array
      const existingPart = spareParts.find(part => 
        part.name.toLowerCase() === name.toLowerCase() ||
        part.code.toLowerCase() === name.toLowerCase()
      );

      if (existingPart) {
        const quantity = window.prompt('Dodavanje količine\n\nKoličina koja se dodaje:', '1');
        if (!quantity || isNaN(quantity)) {
          toast.error('Neispravna količina. Molimo unesite broj.');
          return;
        }

        const additionalQuantity = parseInt(quantity);

        // Check if this part is already in usedSpareParts
        const existingUsedPartIndex = jobData.usedSpareParts ? 
          jobData.usedSpareParts.findIndex(part => part.value === existingPart._id) : -1;

        let updatedParts;
        if (existingUsedPartIndex !== -1) {
          // Update quantity of existing part in the job
          updatedParts = [...(jobData.usedSpareParts || [])];
          updatedParts[existingUsedPartIndex] = {
            ...updatedParts[existingUsedPartIndex],
            quantity: (updatedParts[existingUsedPartIndex].quantity || 0) + additionalQuantity
          };
        } else {
          // Add as new entry to the job
          const newOption = {
            value: existingPart._id,
            label: existingPart.name, // Show only name without description
            price: existingPart.price,
            purchasePrice: existingPart.purchasePrice,
            tax: existingPart.tax,
            quantity: additionalQuantity,
            name: existingPart.name,
            code: existingPart.code
          };
          updatedParts = [...(jobData.usedSpareParts || []), newOption];
        }

        handleSparePartsChange(updatedParts);
        toast.success('Količina rezervnog dela uspešno dodata');
        return;
      }

      // If part doesn't exist, gather information for new part
      const purchasePrice = window.prompt('Dodavanje novog rezervnog dela\n\nNabavna cena (din):', '');
      if (!purchasePrice || isNaN(purchasePrice)) {
        toast.error('Neispravna nabavna cena. Molimo unesite broj.');
        return;
      }

      const price = window.prompt('Dodavanje novog rezervnog dela\n\nProdajna cena (din):', '');
      if (!price || isNaN(price)) {
        toast.error('Neispravna prodajna cena. Molimo unesite broj.');
        return;
      }

      const category = window.prompt('Dodavanje novog rezervnog dela\n\nKategorija:', 'Ostalo');
      if (!category) {
        toast.error('Kategorija je obavezna');
        return;
      }

      const quantity = window.prompt('Dodavanje novog rezervnog dela\n\nKoličina:', '1');
      if (!quantity || isNaN(quantity)) {
        toast.error('Neispravna količina. Molimo unesite broj.');
        return;
      }

      // Generate a unique code using timestamp and random number
      const timestamp = Date.now().toString().slice(-6);
      const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
      const code = `${name.substring(0, 3).toUpperCase()}${timestamp}${random}`;
      
      const sparePartData = {
        name: name.trim(),
        code: code,
        price: parseFloat(price),
        purchasePrice: parseFloat(purchasePrice),
        quantity: parseInt(quantity),
        minQuantity: 1,
        category: category.trim(),
        description: '',
        isActive: true
      };

      const result = await dispatch(createSparePart(sparePartData)).unwrap();
      
      const newOption = {
        value: result._id,
        label: result.name, // Show only name without description
        price: result.price,
        purchasePrice: result.purchasePrice,
        quantity: result.quantity,
        name: result.name,
        code: result.code
      };
      
      setSparePartsOptions(prev => [...prev, newOption]);
      const updatedParts = [...(jobData.usedSpareParts || []), newOption];
      handleSparePartsChange(updatedParts);
      toast.success('Rezervni deo uspešno dodat');
    } catch (error) {
      console.error('Error adding spare part:', error);
      toast.error(`Greška: ${error.message || 'Greška pri dodavanju rezervnog dela'}`);
    }
  };
  
  // Function to check if client exists and create if needed
  const handleClientCreation = async (clientData) => {
    try {
      // Check if client with this phone already exists
      const existingClient = clients?.find(client => 
        client.phone === clientData.phone || 
        client.phone === clientData.phone.replace(/\s/g, '')
      );

      if (existingClient) {
        toast.info(`Klijent "${existingClient.name}" već postoji u bazi`, {
          autoClose: 3000
        });
        return existingClient;
      }

      // Client doesn't exist, create new one
      const newClient = await dispatch(createClientAsync(clientData)).unwrap();
      toast.success(`Novi klijent "${newClient.name}" je sačuvan u bazi`, {
        autoClose: 3000
      });
      return newClient;
    } catch (error) {
      console.error('Error creating client:', error);
      toast.warning('Posao je kreiran, ali klijent nije mogao biti sačuvan u bazi');
      return null;
    }
  };

  const handleBatchCreateAiJobs = async () => {
    if (isEdit) return;
    const indices = aiBatchRowSelected
      .map((sel, i) => (sel ? i : -1))
      .filter((i) => i >= 0);
    if (indices.length === 0) {
      toast.error('Označite bar jedan red.');
      return;
    }

    const confirmed = window.confirm(
      `Kreirati ${indices.length} poslove odjednom? Proverite da su podaci tačni.`
    );
    if (!confirmed) return;

    const flaggedRows = indices.filter((i) => aiExtractedJobsList[i]?.phoneNeedsReview);
    if (flaggedRows.length > 0) {
      toast.error(
        `Ne možete masovno kreirati dok ${flaggedRows.length} red(ova) ima sumnjiv telefon. Koristite "Izmeni" i proverite broj.`
      );
      return;
    }

    setAiBatchSaving(true);
    let created = 0;
    const errors = [];

    try {
      for (const i of indices) {
        const row = aiExtractedJobsList[i];
        const perRowBase = {
          ...jobData,
          clientName: '',
          clientPhone: '',
          clientEmail: '',
          clientAddress: { street: '', number: '', floor: '', apartment: '' },
          issueDescription: '',
          deviceBrand: '',
          deviceModel: '',
          deviceSerialNumber: '',
          serviceDateTime: { date: '', time: '' }
        };
        const merged = applyExtractionToJobData(perRowBase, row);

        const hasPhone = merged.clientPhone && String(merged.clientPhone).trim();
        const hasName = merged.clientName && String(merged.clientName).trim();
        const hasAddr =
          merged.clientAddress &&
          typeof merged.clientAddress === 'object' &&
          Object.values(merged.clientAddress).some((v) => v && String(v).trim());

        if (!hasPhone && !hasName && !hasAddr) {
          errors.push({ index: i + 1, reason: 'Nema klijenta (ime/telefon/adresa)' });
          continue;
        }

        try {
          if (hasPhone) {
            const trimmedClientName = merged.clientName ? merged.clientName.trim() : '';
            const trimmedClientPhone = merged.clientPhone.toString().trim();
            if (!isPhoneStructurallyValid(trimmedClientPhone)) {
              throw new Error(`Neispravan telefon "${trimmedClientPhone}".`);
            }
            if (isLikelyWrongAiPhone(trimmedClientPhone, countryCode)) {
              throw new Error(`Sumnjiv telefon "${trimmedClientPhone}" — ispravite pre čuvanja.`);
            }
            const trimmedClientEmail = merged.clientEmail ? merged.clientEmail.trim() : '';
            await handleClientCreation({
              name: trimmedClientName || trimmedClientPhone,
              phone: trimmedClientPhone,
              email: trimmedClientEmail || undefined,
              address:
                merged.clientAddress && typeof merged.clientAddress === 'object'
                  ? {
                      street: merged.clientAddress.street || '',
                      number: merged.clientAddress.number || '',
                      floor: merged.clientAddress.floor || undefined,
                      apartment: merged.clientAddress.apartment || undefined,
                      isHouse: false,
                      countryCode
                    }
                  : undefined,
              description: undefined
            });
          }

          const payload = buildCreatePayloadFromJobDataForBatch(merged);
          await dispatch(createJob({ jobData: payload })).unwrap();
          created += 1;
        } catch (err) {
          const msg =
            err?.message ||
            err?.data?.message ||
            (typeof err === 'string' ? err : 'Nepoznata greška');
          errors.push({
            index: i + 1,
            name: row?.clientName || '',
            reason: msg
          });
        }
      }

      await dispatch(refreshJobs());

      if (created > 0) {
        toast.success(
          `Kreirano poslova: ${created}${errors.length ? ` · Neuspelo: ${errors.length}` : ''}`
        );
      } else if (errors.length > 0) {
        toast.error('Nijedan posao nije kreiran.');
      }

      if (errors.length > 0) {
        const detail = errors
          .map((e) => `Red ${e.index}${e.name ? ` (${e.name})` : ''}: ${e.reason}`)
          .slice(0, 6)
          .join(' · ');
        toast.info(
          `${detail}${errors.length > 6 ? ` … (+${errors.length - 6})` : ''}`,
          { autoClose: 14000 }
        );
      }

      if (created > 0) {
        setShowAiMultiJobModal(false);
        setAiExtractedJobsList([]);
        setAiEditingBatchIndex(null);
        setAiJobImages([]);
        setAiMessage('');
        setShowAiExtraction(false);
        if (onClose) {
          setTimeout(() => {
            if (typeof onClose === 'function') {
              onClose();
            }
          }, 100);
        }
      }
    } finally {
      setAiBatchSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting || jobState.jobSaving) {
      return;
    }
    
    setIsSubmitting(true);

    const hasUserInput = sectionFieldNames.some((fieldName) => {
      const value = extractFieldValue(jobData, fieldName);
      return fieldHasContent(fieldName, value);
    });

    if (!hasUserInput) {
      toast.error('Unesite makar jedan podatak pre čuvanja posla.');
      setIsSubmitting(false);
      return;
    }

    const addressStringRaw =
      jobData.clientAddress && typeof jobData.clientAddress === 'object'
        ? `${jobData.clientAddress.street || ''} ${jobData.clientAddress.number || ''} ${jobData.clientAddress.floor || ''} ${jobData.clientAddress.apartment || ''}`
      : jobData.clientAddress || '';

    const addressString = addressStringRaw.replace(/\s+/g, ' ').trim();

    let sparePartsArray = [];
    if (Array.isArray(jobData.usedSpareParts)) {
      sparePartsArray = jobData.usedSpareParts
        .map((part) => {
        if (typeof part === 'string') {
          return part;
          }
          if (part && part.value) {
          return part.value;
          }
          if (part && part._id) {
          return part._id;
        }
        return '';
        })
        .filter((id) => id !== '');
    }

    let serviceDateValue = null;
    let scheduledTimeValue = null;
    const dateCleared = !jobData.serviceDateTime?.date || String(jobData.serviceDateTime.date).trim() === '';
    
    // Ako je korisnik obrisao datum (x), ne uzimaj stari datum – posao ide u nezakazane
    if (dateCleared) {
      serviceDateValue = null;
      scheduledTimeValue = null;
    }
    // Inače: datum iz forme
    else if (jobData.serviceDateTime?.date) {
      const [year, month, day] = jobData.serviceDateTime.date.split('-');
      serviceDateValue = new Date(year, month - 1, day, 12, 0, 0);
      scheduledTimeValue = jobData.serviceDateTime.time || null;
    }
    else if (jobData.serviceDate) {
      serviceDateValue = new Date(jobData.serviceDate);
      scheduledTimeValue = jobData.scheduledTime || null;
    }
    else if (isEdit && initialJobData?.serviceDate) {
      serviceDateValue = new Date(initialJobData.serviceDate);
      scheduledTimeValue = initialJobData.scheduledTime || null;
    }

    const assigningLinkedServicePartner =
      !lockAssignmentFields &&
      isLinkedServicePartnerAssignment(jobData.assignedTo, partners);

    if (assigningLinkedServicePartner) {
      serviceDateValue = null;
      scheduledTimeValue = null;
    }

    if (serviceDateValue && scheduledTimeValue && jobData.estimatedDuration) {
      const overlapCheck = checkJobOverlap(
        {
          serviceDate: serviceDateValue,
          scheduledTime: scheduledTimeValue,
          estimatedDuration: parseFloat(jobData.estimatedDuration),
          assignedTo: jobData.assignedTo,
        },
        jobs || [],
        isEdit ? jobData._id : null
      );

      if (overlapCheck.hasOverlap) {
        const overlappingJob = overlapCheck.overlappingJobs[0];
        const endTime = addHoursToTime(scheduledTimeValue, parseFloat(jobData.estimatedDuration));
        const overlappingEndTime = addHoursToTime(overlappingJob.scheduledTime, overlappingJob.estimatedDuration);
        
        toast.warning(
          `⚠️ Preklapanje vremena!\n\nOvaj posao (${formatTimeSlot(scheduledTimeValue, endTime)}) se preklapa sa:\n"${overlappingJob.clientName}" (${formatTimeSlot(overlappingJob.scheduledTime, overlappingEndTime)})\n\nDa li želite da nastavite?`,
          {
            autoClose: 8000,
            position: 'top-center',
          }
        );
        
        const confirmed = window.confirm(
          `⚠️ UPOZORENJE: Preklapanje vremena!\n\n` +
          `Novi posao: ${scheduledTimeValue} - ${endTime} (${jobData.estimatedDuration}h)\n` +
          `Postojeći posao: "${overlappingJob.clientName}" ${overlappingJob.scheduledTime} - ${overlappingEndTime} (${overlappingJob.estimatedDuration}h)\n\n` +
          `Da li želite da nastavite sa kreiranjem posla?`
        );
        
        if (!confirmed) {
          setIsSubmitting(false);
          return;
        }
      }
    }

    const trimmedClientName = jobData.clientName ? jobData.clientName.trim() : '';
    const trimmedClientPhone = jobData.clientPhone ? jobData.clientPhone.toString().trim() : '';
    const trimmedClientEmail = jobData.clientEmail ? jobData.clientEmail.trim() : '';

    if (!isEdit && trimmedClientPhone) {
      if (!isPhoneStructurallyValid(trimmedClientPhone)) {
        toast.error('Broj telefona nema ispravan broj cifara. Proverite pre čuvanja.');
        setIsSubmitting(false);
        return;
      }
      if (isLikelyWrongAiPhone(trimmedClientPhone, countryCode)) {
        toast.error('Sumnjiv broj telefona (verovatno pogrešan prefiks iz AI). Ispravite pre čuvanja.');
        setIsSubmitting(false);
        return;
      }
      if (aiPhoneReview?.needsReview && !trimmedClientPhone) {
        toast.error('Potvrdite ili ispravite broj telefona pre čuvanja posla.');
        setIsSubmitting(false);
        return;
      }
      if (aiPhoneReview?.needsReview) {
        setAiPhoneReview(null);
      }
    }

    if (!isEdit && trimmedClientPhone) {
      const clientDataToSave = {
        name: trimmedClientName || trimmedClientPhone,
        phone: trimmedClientPhone,
        email: trimmedClientEmail || undefined,
        address:
          jobData.clientAddress && typeof jobData.clientAddress === 'object'
            ? {
          street: jobData.clientAddress.street || '',
          number: jobData.clientAddress.number || '',
          floor: jobData.clientAddress.floor || undefined,
          apartment: jobData.clientAddress.apartment || undefined,
          isHouse: false,
                countryCode: countryCode,
              }
            : undefined,
        description: undefined,
      };

      await handleClientCreation(clientDataToSave);
    }

    const effectiveBusinessType = jobData.businessType || businessType;
    const payload = {};

    if (isEdit) {
      if (jobData.status) {
        payload.status = jobData.status;
      }
      if (effectiveBusinessType) {
        payload.businessType = effectiveBusinessType;
      }
      if (jobData.creator) {
        payload.creator = jobData.creator;
      }
    } else {
      payload.status = 'In Pending';
      if (effectiveBusinessType) {
        payload.businessType = effectiveBusinessType;
      }
      payload.creator = user?.result?.email || user?.result?.name || t('common.unknown');
    }

    if (jobData.serviceLocation) {
      payload.serviceLocation = jobData.serviceLocation;
    }
    if (jobData.serviceLocation === 'InWorkshop') {
      if (jobData.serviceArrivalMethodId) {
        payload.serviceArrivalMethodId = jobData.serviceArrivalMethodId;
      }
      if (jobData.serviceArrivalMethodName && jobData.serviceArrivalMethodName.trim()) {
        payload.serviceArrivalMethodName = jobData.serviceArrivalMethodName.trim();
      }
    }

    if (trimmedClientName) {
      payload.clientName = trimmedClientName;
    }

    if (trimmedClientPhone) {
      payload.clientPhone = trimmedClientPhone;
    }

    if (trimmedClientEmail) {
      payload.clientEmail = trimmedClientEmail;
    }

    if (addressString) {
      payload.clientAddress = addressString;
    }
    payload.isBusinessClient = jobData.isBusinessClient === true;
    if (jobData.isBusinessClient && jobData.businessClient && typeof jobData.businessClient === 'object') {
      payload.businessClient = {
        companyName: (jobData.businessClient.companyName || '').trim(),
        contactPerson: (jobData.businessClient.contactPerson || '').trim(),
        pib: (jobData.businessClient.pib || '').trim(),
        registrationNumber: (jobData.businessClient.registrationNumber || '').trim(),
        address: (jobData.businessClient.address || '').trim()
      };
    } else {
      payload.businessClient = {
        companyName: '',
        contactPerson: '',
        pib: '',
        registrationNumber: '',
        address: ''
      };
    }

    if (jobData.deviceCategoryId) {
      payload.deviceCategoryId = jobData.deviceCategoryId;
    }

    if (jobData.deviceCategoryName && jobData.deviceCategoryName.trim()) {
      payload.deviceCategoryName = jobData.deviceCategoryName.trim();
    }

    if (jobData.deviceTypeId) {
      payload.deviceTypeId = jobData.deviceTypeId;
    }

    const resolvedDeviceType = typeof jobData.deviceType === 'string' ? jobData.deviceType.trim() : '';
    if (resolvedDeviceType) {
      payload.deviceType = resolvedDeviceType;
    }

    if (jobData.deviceBrand && jobData.deviceBrand.trim() && jobData.deviceBrand.trim() !== 'Unknown Brand') {
      payload.deviceBrand = jobData.deviceBrand.trim();
    }

    if (jobData.deviceModel && jobData.deviceModel.trim() && jobData.deviceModel.trim() !== 'Unknown Model') {
      payload.deviceModel = jobData.deviceModel.trim();
    }

    if (jobData.deviceSerialNumber && jobData.deviceSerialNumber.trim()) {
      payload.deviceSerialNumber = jobData.deviceSerialNumber.trim();
    }

    if (jobData.serviceId) {
      payload.serviceId = jobData.serviceId;
    }

    if (jobData.serviceName && jobData.serviceName.trim()) {
      payload.serviceName = jobData.serviceName.trim();
    }

    if (jobData.servicePrice !== '' && jobData.servicePrice !== null && jobData.servicePrice !== undefined) {
      const numericPrice = Number(jobData.servicePrice);
      if (Number.isFinite(numericPrice)) {
        payload.servicePrice = numericPrice;
      }
    }

    if (
      jobData.serviceDurationMinutes !== undefined &&
      jobData.serviceDurationMinutes !== null &&
      jobData.serviceDurationMinutes !== ''
    ) {
      const durationMinutes = Number(jobData.serviceDurationMinutes);
      if (Number.isFinite(durationMinutes)) {
        payload.serviceDurationMinutes = durationMinutes;
      }
    }

    if (Array.isArray(jobData.deviceImages) && jobData.deviceImages.length > 0 && canUseJobImages) {
      payload.deviceImages = jobData.deviceImages.slice(0, maxJobImages);
    }

    if (jobData.issueDescription && jobData.issueDescription.trim()) {
      payload.issueDescription = jobData.issueDescription.trim();
    }

    if (jobData.priority && jobData.priority.trim()) {
      const normalizedPriority =
        jobData.priority.charAt(0).toUpperCase() + jobData.priority.slice(1).toLowerCase();
      payload.priority = normalizedPriority;
    }

    if (jobData.warranty === true || jobData.warranty === 'yes') {
      payload.hasWarranty = true;
      if (jobData.warrantyPurchaseDate) {
        payload.warrantyPurchaseDate = new Date(jobData.warrantyPurchaseDate);
      }
    } else {
      payload.hasWarranty = false;
      payload.warrantyPurchaseDate = null;
    }

    if (
      jobData.estimatedDuration !== undefined &&
      jobData.estimatedDuration !== null &&
      jobData.estimatedDuration !== ''
    ) {
      const estimatedDurationNumeric = parseFloat(jobData.estimatedDuration);
      if (Number.isFinite(estimatedDurationNumeric)) {
        payload.estimatedDuration = estimatedDurationNumeric;
      }
    }

    if (jobData.notes && jobData.notes.trim()) {
      payload.notes = jobData.notes.trim();
    }

    if (isEdit) {
      payload.clientNotifiedAt = (jobData.clientNotifiedAt && String(jobData.clientNotifiedAt).trim())
        ? new Date(jobData.clientNotifiedAt).toISOString()
        : null;
    }

    // Uvek šaljemo assignedTo pri izmeni – osim kada je prosleđivanje aktivno
    if (!lockAssignmentFields) {
      const assignedToValue = jobData.assignedTo && String(jobData.assignedTo).trim() && jobData.assignedTo !== 'Unassigned'
        ? jobData.assignedTo
        : 'Unassigned';
      payload.assignedTo = assignedToValue;
    }

    if (sparePartsArray.length > 0) {
      payload.usedSpareParts = sparePartsArray;
    }

    // serviceDate i scheduledTime: ako je datum obrisan ili posao ide partneru, šaljemo null
    if (assigningLinkedServicePartner) {
      payload.serviceDate = null;
      payload.scheduledTime = null;
    } else if (serviceDateValue) {
      payload.serviceDate = serviceDateValue;
      if (scheduledTimeValue) payload.scheduledTime = scheduledTimeValue;
    } else if (isEdit && dateCleared) {
      payload.serviceDate = null;
      payload.scheduledTime = null;
    } else if (isEdit && initialJobData?.serviceDate) {
      payload.serviceDate = new Date(initialJobData.serviceDate);
      payload.scheduledTime = initialJobData.scheduledTime || null;
    }
    if (scheduledTimeValue && !dateCleared) {
      payload.scheduledTime = scheduledTimeValue;
    }

    // Za arhivirane/završene poslove: pošalji datum i vreme završetka (report.completedAt)
    if (isEdit && (jobData.status === 'Archived' || jobData.status === 'Finished') && jobData.completedDateTime?.date) {
      const completedDate = jobData.completedDateTime.date;
      const completedTime = (jobData.completedDateTime.time || '00:00').substring(0, 5);
      const completedAt = new Date(`${completedDate}T${completedTime}:00`);
      if (!isNaN(completedAt.getTime())) {
        payload.report = {
          ...(initialJobData?.report || {}),
          completedAt: completedAt.toISOString()
        };
      }
    }

    try {
      const batchEditIndexAtSubmit = aiEditingBatchIndex;
      const batchListAtSubmit = aiExtractedJobsList;

      if (isEdit) {
        if (!jobData?._id) {
          console.error('❌ Cannot update job: jobData._id is undefined');
          toast.error('Greška: ID posla nije definisan');
          setIsSubmitting(false);
          return;
        }

        const canCloseJob = await confirmOpenWithdrawnItemsBeforeJobClose(
          jobData._id,
          initialJobData?.status,
          jobData.status
        );
        if (!canCloseJob) {
          setIsSubmitting(false);
          return;
        }

        const result = await dispatch(updateJob({ id: jobData._id, jobData: payload })).unwrap();
        console.log('✅ Job updated successfully:', result);

        const wasAlreadyForwarded =
          initialJobData?.handoffStatus === 'pending_acceptance' &&
          initialJobData?.assignedPartnerCompanyId &&
          String(initialJobData.assignedPartnerCompanyId) === String(result?.assignedPartnerCompanyId || '');

        if (
          result?.handoffStatus === 'pending_acceptance' &&
          result?.assignedPartnerCompanyId &&
          !wasAlreadyForwarded
        ) {
          const partnerName =
            getPartnerFromAssignedTo(result.assignedTo, partners)?.companyName ||
            getPartnerFromAssignedTo(result.assignedTo, partners)?.name ||
            'servisnom partneru';
          toast.success(
            `Posao je poslat ${partnerName}. Pratite status u sekciji „Prosleđeni poslovi”.`,
            { autoClose: 8000 }
          );
        } else {
          toast.success('Posao je uspešno ažuriran');
        }
        
        if (
          jobData.status === 'Archived' ||
          jobData.status === 'Finished' ||
          payload.report?.completedAt
        ) {
          await dispatch(refreshArchiveJobs());
        }
        
        // Close modal - always call onClose if provided
        if (onClose) {
          console.log('📝 Calling onClose after successful update');
          // Use setTimeout to ensure state updates complete before closing
          setTimeout(() => {
            if (typeof onClose === 'function') {
              onClose();
            }
          }, 100);
        }
      } else {
        const created = await dispatch(createJob({ jobData: payload })).unwrap();
        if (
          created?.handoffStatus === 'pending_acceptance' &&
          created?.assignedPartnerCompanyId
        ) {
          const partnerName =
            getPartnerFromAssignedTo(created.assignedTo, partners)?.companyName ||
            getPartnerFromAssignedTo(created.assignedTo, partners)?.name ||
            'servisnom partneru';
          toast.success(
            `Posao je poslat ${partnerName}. Pratite status u sekciji „Prosleđeni poslovi”.`,
            { autoClose: 8000 }
          );
        } else {
          toast.success('Posao je uspešno kreiran');
        }

        const batchEditIndex = batchEditIndexAtSubmit;
        const hasRemainingBatchRows =
          batchEditIndex !== null &&
          batchEditIndex >= 0 &&
          batchListAtSubmit.length > 0;

        if (hasRemainingBatchRows) {
          const remaining = batchListAtSubmit.filter((_, i) => i !== batchEditIndex);
          setAiExtractedJobsList(remaining);
          setAiEditingBatchIndex(null);
          setAiPhoneReview(null);
          setJobData((prev) => clearClientFieldsFromJobData(prev));

          if (remaining.length > 0) {
            toast.info(
              `Preostalo ${remaining.length} red(ova) u AI tabeli. Nastavite sa ostalim poslovima.`,
              { autoClose: 6000 }
            );
            setShowAiMultiJobModal(true);
          } else {
            setAiJobImages([]);
            setAiMessage('');
            setShowAiExtraction(false);
            if (onClose) {
              setTimeout(() => {
                if (typeof onClose === 'function') {
                  onClose();
                }
              }, 100);
            }
          }
        } else if (onClose) {
          console.log('📝 Calling onClose after successful update');
          setTimeout(() => {
            if (typeof onClose === 'function') {
              onClose();
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error('❌ Error submitting job:', error);
      const errorMessage =
        error?.message ||
        error?.data?.message ||
        error?.response?.data?.message ||
        error?.error ||
        (typeof error === 'string' ? error : null) ||
        'Greška pri čuvanju posla. Molimo pokušajte ponovo.';
      toast.error(errorMessage);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Update input class for smaller size
  const inputClass = "mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white placeholder-gray-400 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-[3px] px-2";

  // Zakonski rokovi – samo za aktivne poslove; kod završenih nema smisla pri izmeni
  const showLegalDeadlinesSection =
    isEdit &&
    jobData.status !== 'Archived' &&
    jobData.status !== 'Finished' &&
    jobData.status !== 'Completed';

    return (
    <form onSubmit={handleSubmit} className={`w-full ${className}`} noValidate>
      {isModal && (
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-200">{formTitle}</h2>
          {!isEdit && initialJobData?.displayDateTime && (
            <p className="text-sm text-gray-400 mt-1">
              {t('jobs.fillJobDetails')}
            </p>
          )}
        </div>
      )}

      {/* AI Extraction Section */}
      {!isEdit && (
        <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
          <button
            type="button"
            onClick={() => setShowAiExtraction(!showAiExtraction)}
            className="w-full text-left focus:outline-none"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center">
                <svg
                  className={`w-5 h-5 mr-2 text-blue-400 transition-transform flex-shrink-0 ${showAiExtraction ? 'transform rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                <span className="text-sm font-medium text-gray-200">
                  🤖 AI ekstrakcija (tekst i/ili slike)
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400 leading-relaxed pl-7 sm:pl-0">
                <span>Tekst i slike zajedno; sa spiska može više naloga (Business)</span>
                <span>·</span>
                <span className={canUseVoiceInput ? '' : 'text-gray-500'}>glas</span>
                <button
                  type="button"
                  aria-disabled={!canUseVoiceInput}
                  onClick={() => {
                    if (!canUseVoiceInput) {
                      toast.info(voiceInputRestrictionMsg || 'Glasovni unos je od Standard paketa.');
                      return;
                    }
                    setShowVoiceInputModal(true);
                  }}
                  className={`p-1 transition-colors ${
                    canUseVoiceInput
                      ? 'text-gray-400 hover:text-blue-400'
                      : 'text-gray-600 cursor-not-allowed opacity-60'
                  }`}
                  title={canUseVoiceInput ? 'Glasovni unos' : voiceInputRestrictionMsg || 'Standard+'}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </button>

          {showAiExtraction && (
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tekst poruke / uputstva (isti unos koristi se zajedno sa slikama):
                </label>
                <textarea
                  value={aiMessage}
                  onChange={(e) => setAiMessage(e.target.value)}
                  placeholder="Poruka ili kratko uputstvo..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={4}
                  disabled={aiLoading}
                />

                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Slike (kalendar, tabela, screenshot; do {MAX_AI_JOB_IMAGES}) — od Starter paketa
                  </label>
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-xs cursor-pointer">
                    Dodaj slike
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={!canUseAI || aiLoading}
                      onChange={(e) => {
                        handleAiJobImagesPick(e.target.files);
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                  {aiJobImages?.length > 0 && (
                    <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                      {aiJobImages.map((file, idx) => (
                        <li
                          key={`${file.name}-${file.lastModified}-${idx}`}
                          className="flex items-center justify-between gap-2 text-xs text-gray-300 bg-gray-900/60 rounded px-2 py-1"
                        >
                          <span className="truncate" title={file.name}>
                            {idx + 1}. {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeAiJobImageAt(idx)}
                            disabled={aiLoading}
                            className="shrink-0 text-red-400 hover:text-red-300 disabled:opacity-50"
                            aria-label="Ukloni sliku"
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {aiJobImages?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Ukupno slika: {aiJobImages.length} / {MAX_AI_JOB_IMAGES}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleAiExtraction}
                disabled={
                  aiLoading ||
                  (!aiMessage.trim() && (!aiJobImages || aiJobImages.length === 0))
                }
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                title={
                  !canUseAI && aiJobImages?.length > 0
                    ? 'Sa slikama je potreban Starter ili viši paket'
                    : ''
                }
              >
                {aiLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Procesiranje...
                  </>
                ) : (
                  'Ekstrahuj (tekst i/ili slike)'
                )}
              </button>

              <p className="text-xs text-gray-400">Pošalji tekst i/ili slike. Ako AI vrati više redova, izaberi u tabeli.</p>

              {aiExtractedJobsList.length > 0 && !showAiMultiJobModal && (
                <div className="mt-3 p-3 rounded-lg border border-amber-700/50 bg-amber-900/15 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs text-amber-100/90">
                    Sakrivena tabela ({aiExtractedJobsList.length}).
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowAiMultiJobModal(true)}
                    className="shrink-0 text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md font-medium"
                  >
                    Otvori tabelu
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {aiPhoneReview?.needsReview && (
        <div className="mb-4 p-4 rounded-lg border border-amber-600 bg-amber-900/25">
          <div className="flex flex-col sm:flex-row gap-4">
            {aiPhoneReview.imageSrc && aiPhoneReview.cropBox && (
              <PhoneCropPreview
                imageSrc={aiPhoneReview.imageSrc}
                cropBox={aiPhoneReview.cropBox}
                className="shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-200 mb-2">
                Potvrdite broj telefona
              </p>
              <div className="text-xs text-amber-100/90 space-y-1">
                {aiPhoneReview.primary && (
                  <p>
                    AI čitanje: <span className="font-mono">{aiPhoneReview.primary}</span>
                  </p>
                )}
                {aiPhoneReview.verifier && aiPhonesMismatch(aiPhoneReview.primary, aiPhoneReview.verifier) && (
                  <p>
                    Ponovno AI: <span className="font-mono text-red-300">{aiPhoneReview.verifier}</span>
                  </p>
                )}
                {aiPhoneReview.ocr && (
                  <p>
                    OCR sa slike: <span className="font-mono">{aiPhoneReview.ocr}</span>
                  </p>
                )}
                {aiPhoneReview.issues?.map((issue, i) => (
                  <p key={i}>{issue.message}</p>
                ))}
              </div>
              <p className="text-xs text-amber-200/80 mt-2">
                Ispravite polje telefona ako treba, zatim sačuvajte posao.
              </p>
            </div>
          </div>
        </div>
      )}

      {showAiMultiJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-600 max-w-5xl w-full max-h-[88vh] overflow-hidden flex flex-col shadow-xl">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center gap-2">
              <div>
                <h3 className="text-lg font-semibold text-white">Više intervencija iz AI</h3>
                <p className="text-xs text-gray-400 mt-1">
                  Izmeni red ili kreiraj označene.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAiMultiJobModal(false)}
                className="text-gray-400 hover:text-white p-2 rounded"
                aria-label="Zatvori"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-auto p-4">
              <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 mb-3 py-2 -mt-2 bg-gray-800 text-sm border-b border-gray-700/80">
                <span className="text-gray-400">
                  Označeno:{' '}
                  {aiBatchRowSelected.filter(Boolean).length} / {aiExtractedJobsList.length}
                </span>
                <button
                  type="button"
                  onClick={() => setAiBatchRowSelected(aiExtractedJobsList.map(() => true))}
                  className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded"
                >
                  Označi sve
                </button>
                <button
                  type="button"
                  onClick={() => setAiBatchRowSelected(aiExtractedJobsList.map(() => false))}
                  className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded"
                >
                  Poništi sve
                </button>
                <button
                  type="button"
                  onClick={handleBatchCreateAiJobs}
                  disabled={
                    aiBatchSaving ||
                    jobState.jobSaving ||
                    aiBatchRowSelected.filter(Boolean).length === 0
                  }
                  className="px-3 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded ml-auto"
                >
                  {aiBatchSaving ? 'Kreiranje…' : 'Kreiraj označene poslove'}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-300 border border-gray-700 rounded-lg">
                  <thead className="bg-gray-900 text-gray-400 uppercase text-xs">
                    <tr>
                      <th className="px-2 py-2 w-10">
                        <span className="sr-only">Biraj</span>
                      </th>
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Klijent</th>
                      <th className="px-3 py-2">AI #1</th>
                      <th className="px-3 py-2">Ponovno</th>
                      <th className="px-3 py-2">Telefon</th>
                      <th className="px-3 py-2">Adresa</th>
                      <th className="px-3 py-2">Kvar / uređaj</th>
                      <th className="px-3 py-2">Termin</th>
                      <th className="px-3 py-2">Izmena</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiExtractedJobsList.map((row, idx) => {
                      const addr = row.clientAddress;
                      const primaryPhone = row.clientPhoneFromPrimaryAI || row.clientPhoneRaw || '';
                      const verifierPhone = row.clientPhoneFromVerifierAI || '';
                      const phoneFlagged = Boolean(row.phoneNeedsReview);
                      const addrStr = addr
                        ? [addr.street, addr.number, addr.floor, addr.apartment]
                            .filter(Boolean)
                            .join(', ')
                        : '';
                      const dev = [row.deviceBrand, row.deviceModel].filter(Boolean).join(' ');
                      const term =
                        row.serviceDate || row.serviceTime
                          ? [
                              row.serviceDate ? formatIsoYmdToEuropean(row.serviceDate) : '',
                              row.serviceTime || ''
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : '—';
                      return (
                        <tr
                          key={idx}
                          className={`border-t border-gray-700 hover:bg-gray-700/40 ${
                            phoneFlagged ? 'bg-amber-900/20' : ''
                          }`}
                        >
                          <td className="px-2 py-2 align-middle">
                            <input
                              type="checkbox"
                              className="rounded border-gray-600"
                              checked={!!aiBatchRowSelected[idx]}
                              onChange={() => {
                                setAiBatchRowSelected((prev) => {
                                  const base = aiExtractedJobsList.map((_, j) =>
                                    j < prev.length ? prev[j] : true
                                  );
                                  base[idx] = !base[idx];
                                  return base;
                                });
                              }}
                              aria-label={`Označi red ${idx + 1}`}
                            />
                          </td>
                          <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                          <td className="px-3 py-2">{row.clientName || '—'}</td>
                          <td className="px-3 py-2 text-xs font-mono whitespace-nowrap">
                            {primaryPhone || '—'}
                          </td>
                          <td
                            className={`px-3 py-2 text-xs font-mono whitespace-nowrap ${
                              aiPhonesMismatch(primaryPhone, verifierPhone)
                                ? 'text-red-300 font-semibold'
                                : ''
                            }`}
                          >
                            {verifierPhone || '—'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {phoneFlagged &&
                                aiExtractionSourcePreviews[row.phoneCropBox?.imageIndex ?? 0] &&
                                row.phoneCropBox && (
                                  <PhoneCropPreview
                                    imageSrc={
                                      aiExtractionSourcePreviews[row.phoneCropBox?.imageIndex ?? 0]
                                    }
                                    cropBox={row.phoneCropBox}
                                    className="shrink-0"
                                  />
                                )}
                              <span>
                                {row.clientPhone || (
                                  <span className="text-amber-300 text-xs">potvrdi</span>
                                )}
                              </span>
                            </div>
                            {phoneFlagged && (
                              <span className="block text-[10px] text-amber-300 mt-0.5">
                                provera telefona
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 max-w-[180px]">{addrStr || '—'}</td>
                          <td className="px-3 py-2 max-w-[200px]">
                            {(row.issueDescription || '').slice(0, 80)}
                            {(row.issueDescription || '').length > 80 ? '…' : ''}
                            {dev ? <span className="block text-xs text-gray-500 mt-0.5">{dev}</span> : null}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs">{term}</td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => {
                                setJobData((prev) => buildJobDataFromExtractedRow(row, prev));
                                setAiEditingBatchIndex(idx);
                                setAiPhoneReview(
                                  row.phoneNeedsReview
                                    ? buildPhoneReviewFromExtractedJob(row, aiExtractionSourcePreviews)
                                    : null
                                );
                                setShowAiMultiJobModal(false);
                                toast.info(
                                  `Red ${idx + 1} je u formi ispod — ispravite podatke po potrebi i sačuvajte posao.`
                                );
                              }}
                              disabled={aiBatchSaving}
                              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs rounded"
                            >
                              Izmeni
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowAiMultiJobModal(false);
                  setAiExtractedJobsList([]);
                  setAiEditingBatchIndex(null);
                  setAiPhoneReview(null);
                }}
                disabled={aiBatchSaving}
                className="px-4 py-2 border border-gray-600 bg-gray-900 hover:bg-gray-800 text-gray-200 rounded-md text-sm disabled:opacity-50"
              >
                Odustani
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice Input Modal */}
      {showVoiceInputModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">Unesi tekstualnu poruku</h3>
                <p className="text-gray-400 text-sm mt-1">Unesite tekstualnu poruku ili snimite glasovnu poruku</p>
              </div>
              <button
                onClick={() => {
                  if (recognition && isRecording) {
                    recognition.stop();
                    setIsRecording(false);
                    isRecordingRef.current = false;
                  }
                  setShowVoiceInputModal(false);
                  setVoiceInputText('');
                  finalTranscriptsRef.current = [];
                  lastFinalIndexRef.current = -1;
                }}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tekstualna poruka:
                </label>
                <textarea
                  value={voiceInputText}
                  onChange={(e) => setVoiceInputText(e.target.value)}
                  placeholder="Unesite tekstualnu poruku ovde ili kliknite na mikrofon da snimite..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={6}
                  autoFocus
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (!recognition) {
                      toast.error('Glasovni unos nije podržan u ovom pregledaču');
                      return;
                    }
                    if (isRecording) {
                      recognition.stop();
                      setIsRecording(false);
                      isRecordingRef.current = false;
                      finalTranscriptsRef.current = [];
                      lastFinalIndexRef.current = -1;
                      toast.info('Snimanje zaustavljeno');
                    } else {
                      try {
                        // Request microphone permission if not already granted
                        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                          navigator.mediaDevices.getUserMedia({ audio: true })
                            .then(() => {
                              console.log('✅ Microphone permission granted, starting recognition');
                              setVoiceInputText('');
                              finalTranscriptsRef.current = [];
                              lastFinalIndexRef.current = -1;
                              // Set recording state BEFORE starting recognition
                              console.log('📝 Setting recording state to true BEFORE start()');
                              setIsRecording(true);
                              isRecordingRef.current = true;
                              console.log('📝 Recording state set:', {
                                state: true,
                                ref: isRecordingRef.current
                              });
                              
                              // Small delay to ensure state is set before starting
                              setTimeout(() => {
                                try {
                                  console.log('🚀 Calling recognition.start()...');
                                  recognition.start();
                                  toast.success('Snimanje započeto...');
                                  console.log('✅ Recognition.start() called successfully');
                                } catch (startError) {
                                  console.error('❌ Error starting recognition:', startError);
                                  toast.error('Greška pri pokretanju snimanja: ' + startError.message);
                                  setIsRecording(false);
                                  isRecordingRef.current = false;
                                }
                              }, 50); // Small delay to ensure state is set
                            })
                            .catch((error) => {
                              console.error('Microphone permission error:', error);
                              toast.error('Dozvola za mikrofon je potrebna. Molimo dozvolite pristup mikrofonu.');
                              setIsRecording(false);
                              isRecordingRef.current = false;
                            });
                        } else {
                          // Fallback for browsers without getUserMedia
                          console.log('⚠️ getUserMedia not available, starting recognition directly');
                          setVoiceInputText('');
                          finalTranscriptsRef.current = [];
                          lastFinalIndexRef.current = -1;
                          // Set recording state BEFORE starting recognition
                          setIsRecording(true);
                          isRecordingRef.current = true;
                          try {
                            recognition.start();
                            toast.success('Snimanje započeto...');
                            console.log('✅ Recognition started successfully (fallback)');
                          } catch (startError) {
                            console.error('❌ Error starting recognition:', startError);
                            toast.error('Greška pri pokretanju snimanja: ' + startError.message);
                            setIsRecording(false);
                            isRecordingRef.current = false;
                          }
                        }
                      } catch (error) {
                        console.error('Error starting recognition:', error);
                        toast.error('Greška pri pokretanju snimanja: ' + error.message);
                        setIsRecording(false);
                        isRecordingRef.current = false;
                      }
                    }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                    isRecording
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {isRecording ? (
                    <>
                      <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                        <circle cx="12" cy="12" r="3" fill="currentColor"/>
                      </svg>
                      Zaustavi snimanje
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      Snimi glasovnu poruku
                    </>
                  )}
                </button>
                {isRecording && (
                  <div className="flex items-center gap-2 text-red-400">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm">Snima se...</span>
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (recognition && isRecording) {
                      recognition.stop();
                      setIsRecording(false);
                      isRecordingRef.current = false;
                    }
                    if (voiceInputText.trim()) {
                      setAiMessage(voiceInputText.trim());
                      setShowVoiceInputModal(false);
                      setVoiceInputText('');
                    }
                  }}
                  disabled={!voiceInputText.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Koristi ovu poruku
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (recognition && isRecording) {
                      recognition.stop();
                      setIsRecording(false);
                      isRecordingRef.current = false;
                    }
                    setShowVoiceInputModal(false);
                    setVoiceInputText('');
                    finalTranscriptsRef.current = [];
                    lastFinalIndexRef.current = -1;
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-md transition-colors"
                >
                  Otkaži
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Datum i vreme završetka – samo za arhivirane/završene, iznad detalja stranke i uređaja */}
      {isEdit && (jobData.status === 'Archived' || jobData.status === 'Finished') && (
        <div className="mb-6 rounded-lg bg-gray-800 p-4 border border-gray-700">
          <h3 className="text-sm font-semibold text-gray-200 mb-3">Datum i vreme završetka stranke</h3>
          <p className="text-xs text-gray-400 mb-3">
            Dan i vreme kada je intervencija završena i arhivirana (ne dan zakazivanja).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Datum završetka</label>
              <DatePickerField
                value={jobData.completedDateTime?.date || ''}
                onChange={(newDate) =>
                  setJobData((prev) => ({
                    ...prev,
                    completedDateTime: {
                      ...(prev.completedDateTime || { date: '', time: '00:00' }),
                      date: newDate
                    }
                  }))
                }
                placeholder="dd.mm.yyyy"
                inputClass="w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Vreme završetka</label>
              <TimePickerField
                value={jobData.completedDateTime?.time || '00:00'}
                onChange={(time) =>
                  setJobData((prev) => ({
                    ...prev,
                    completedDateTime: {
                      ...(prev.completedDateTime || { date: '', time: '00:00' }),
                      time
                    }
                  }))
                }
                inputClass={inputClass}
                placeholder="HH:MM"
              />
            </div>
          </div>
        </div>
      )}

      {/* Form sections */}
      {lockAssignmentFields && (
        <p className="mb-3 text-xs text-amber-300 bg-amber-900/20 border border-amber-700/50 rounded px-3 py-2">
          Radnik, tim i servisni partner su zaključani dok podeservis ne prihvati ili odbije prosleđivanje. Ostala polja možete i dalje menjati.
        </p>
      )}
      {isMobile ? (
        <div className="space-y-3">
          {SECTION_KEYS.map((sectionKey) => {
            const section = sections[sectionKey];
            if (!section) return null;

            return (
              <JobFormSection
                key={sectionKey}
                title={section.title}
                fields={section.fields}
                formConfig={resolvedFormConfig}
                jobData={jobData}
                inputClass={inputClass}
                workerOptions={workerOptions}
                teamOptions={teamOptions}
                partnerOptions={partnerOptions}
                sparePartsOptions={sparePartsOptions}
                serviceLocation={jobData.serviceLocation}
                onChange={handleFieldChange}
                onQuickAddSparePart={handleQuickAddSparePart}
                onQuickAddCategory={handleQuickAddCategory}
                onQuickAddService={handleQuickAddService}
                onQuickAddArrivalMethod={handleQuickAddArrivalMethod}
                suggestions={suggestions}
                showSuggestions={showSuggestions}
                onSuggestionSelect={handleSuggestionSelect}
                lockAssignmentFields={lockAssignmentFields}
                variant="accordion"
                isOpen={openSections[sectionKey]}
                onToggle={() => toggleSection(sectionKey)}
                onOpenDeviceImages={canUseJobImages ? openDeviceImagesPicker : undefined}
                onOpenDeviceQr={() => setShowDeviceQrModal(true)}
              />
            );
          })}
          {showLegalDeadlinesSection && (() => {
            const mockJobRokovi = { createdAt: jobData.createdAt, clientNotifiedAt: jobData.clientNotifiedAt || null, status: jobData.status };
            const markerRokovi = getDeadlineMarker(mockJobRokovi);
            const formatDateTime = (dateVal) => {
              if (!dateVal) return '—';
              const d = new Date(dateVal);
              const day = String(d.getDate()).padStart(2, '0');
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const h = String(d.getHours()).padStart(2, '0');
              const min = String(d.getMinutes()).padStart(2, '0');
              return `${day}.${month}.${d.getFullYear()} ${h}:${min}`;
            };
            const legalOpen = Boolean(openSections.legalDeadlines);
            return (
              <div className="mt-1 rounded-md border border-gray-700 bg-gray-900 w-full">
                <button
                  type="button"
                  onClick={() => toggleSection('legalDeadlines')}
                  className="flex w-full items-center justify-between px-3 py-2 text-left"
                >
                  <span className="text-sm font-medium text-gray-100">Zakonski rokovi</span>
                  <svg
                    className={`h-4 w-4 text-gray-400 transition-transform transform ${legalOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {legalOpen && (
                  <div className="border-t border-gray-800 px-3 py-3 space-y-3">
                    <div className="flex flex-col gap-2 w-full">
                      <div className="flex flex-col xs:flex-row gap-1.5 xs:items-center min-w-0">
                        <label className="text-xs text-gray-400 shrink-0">Datum obaveštavanja</label>
                        <div className="w-full max-w-[11rem]">
                          <DatePickerField
                            value={jobData.clientNotifiedAt || ''}
                            onChange={(dateStr) => handleFieldChange('clientNotifiedAt', dateStr)}
                            placeholder="dd.mm.yyyy"
                            inputClass="w-full bg-gray-700 border border-gray-600 rounded px-1.5 py-1 text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col xs:flex-row gap-1.5 xs:items-center min-w-0">
                        <span className="text-xs text-gray-400 shrink-0">Status:</span>
                        <span className="bg-gray-700 rounded px-1.5 py-0.5 text-xs text-gray-300 inline-flex items-center gap-1 max-w-full" title={markerRokovi?.label || ''}>
                          {markerRokovi ? (
                            <>
                              <span className="truncate">{markerRokovi.shortText || markerRokovi.shortLabel}</span>
                              <DeadlineMarker job={mockJobRokovi} size="sm" />
                            </>
                          ) : '—'}
                        </span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-gray-700 grid grid-cols-1 gap-2 w-full">
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs text-gray-400">Kreirano:</span>
                        <span className="text-xs text-gray-300 break-words">{formatDateTime(jobData.createdAt)}</span>
                      </div>
                      {jobData.creator && (
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs text-gray-400">Kreirao:</span>
                          <span className="text-xs text-gray-300 break-words">{jobData.creator}</span>
                        </div>
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs text-gray-400">Ažurirano:</span>
                        <span className="text-xs text-gray-300 break-words">{formatDateTime(jobData.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
      </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:items-start lg:gap-y-0" id="job-form-sections-grid">
          {/* Client Info - First column, spans full height */}
          {sections.clientInfo && (
            <div className="lg:row-span-2">
              <JobFormSection
                key="clientInfo"
                title={sections.clientInfo.title}
                fields={sections.clientInfo.fields}
                formConfig={resolvedFormConfig}
                jobData={jobData}
                inputClass={inputClass}
                workerOptions={workerOptions}
                teamOptions={teamOptions}
                partnerOptions={partnerOptions}
                sparePartsOptions={sparePartsOptions}
                serviceLocation={jobData.serviceLocation}
                onChange={handleFieldChange}
                onQuickAddSparePart={handleQuickAddSparePart}
                onQuickAddCategory={handleQuickAddCategory}
                onQuickAddService={handleQuickAddService}
                onQuickAddArrivalMethod={handleQuickAddArrivalMethod}
                suggestions={suggestions}
                showSuggestions={showSuggestions}
                onSuggestionSelect={handleSuggestionSelect}
                lockAssignmentFields={lockAssignmentFields}
              />
            </div>
          )}
          
          {/* Device Info, Service Info, Additional Info - Columns 2, 3, 4 */}
          {['deviceInfo', 'serviceInfo', 'additionalInfo'].map((sectionKey) => {
            const section = sections[sectionKey];
            if (!section) return null;
            
            // Filter out issueDescription from deviceInfo section for desktop (will render separately)
            const fieldsToRender = sectionKey === 'deviceInfo' 
              ? section.fields.filter(f => f !== 'issueDescription')
              : section.fields;
            
            if (fieldsToRender.length === 0) return null;

            return (
              <JobFormSection
                key={sectionKey}
                title={section.title}
                fields={fieldsToRender}
                formConfig={resolvedFormConfig}
                jobData={jobData}
                inputClass={inputClass}
                workerOptions={workerOptions}
                teamOptions={teamOptions}
                partnerOptions={partnerOptions}
                sparePartsOptions={sparePartsOptions}
                serviceLocation={jobData.serviceLocation}
                onChange={handleFieldChange}
                onQuickAddSparePart={handleQuickAddSparePart}
                onQuickAddCategory={handleQuickAddCategory}
                onQuickAddService={handleQuickAddService}
                onQuickAddArrivalMethod={handleQuickAddArrivalMethod}
                suggestions={suggestions}
                showSuggestions={showSuggestions}
                onSuggestionSelect={handleSuggestionSelect}
                lockAssignmentFields={lockAssignmentFields}
                onOpenDeviceImages={canUseJobImages ? openDeviceImagesPicker : undefined}
                onOpenDeviceQr={() => setShowDeviceQrModal(true)}
              />
            );
          })}
          
          {/* Issue Description - spans columns 2-4 (deviceInfo, serviceInfo, additionalInfo) on desktop, in second row */}
          {sections.deviceInfo?.fields.includes('issueDescription') && (
            <div className="lg:col-start-2 lg:col-span-3">
              <div className="rounded-lg bg-gray-800 p-4 lg:-mt-4">
                <FieldRenderer
                  fieldName="issueDescription"
                  fieldConfig={{
                    ...resolvedFormConfig.issueDescription,
                    rows: 2
                  }}
                  value={jobData.issueDescription}
                  onChange={handleFieldChange}
                  inputClass={inputClass}
                  workerOptions={workerOptions}
                  teamOptions={teamOptions}
                  partnerOptions={partnerOptions}
                  sparePartsOptions={sparePartsOptions}
                  serviceLocation={jobData.serviceLocation}
                  onQuickAddSparePart={handleQuickAddSparePart}
                  onQuickAddCategory={handleQuickAddCategory}
                  onQuickAddService={handleQuickAddService}
                  onQuickAddArrivalMethod={handleQuickAddArrivalMethod}
                  jobData={jobData}
                  suggestions={suggestions?.issueDescription || []}
                  showSuggestions={showSuggestions?.issueDescription || false}
                  onSuggestionSelect={handleSuggestionSelect ? (client) => handleSuggestionSelect(client, 'issueDescription') : undefined}
                />
              </div>
            </div>
          )}

          {/* Zakonski rokovi – obaveštavanje stranke i tok servisa (samo pri uređivanju aktivnog posla) */}
          {showLegalDeadlinesSection && (() => {
            const mockJobRokovi = { createdAt: jobData.createdAt, clientNotifiedAt: jobData.clientNotifiedAt || null, status: jobData.status };
            const markerRokovi = getDeadlineMarker(mockJobRokovi);
            const formatDateTime = (dateVal) => {
              if (!dateVal) return '—';
              const d = new Date(dateVal);
              const day = String(d.getDate()).padStart(2, '0');
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const h = String(d.getHours()).padStart(2, '0');
              const min = String(d.getMinutes()).padStart(2, '0');
              return `${day}.${month}.${d.getFullYear()} ${h}:${min}`;
            };
            return (
              <div className="lg:col-start-1 lg:col-span-4 rounded-lg bg-gray-800 border border-gray-700 p-2.5 w-full">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 w-full">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1 basis-0">
                    <span className="text-xs font-semibold text-white shrink-0">Zakonski rokovi</span>
                  </div>
                  <div className="flex items-center gap-1.5 min-w-0 flex-1 basis-0">
                    <label className="text-xs text-gray-400 shrink-0">Datum obaveštavanja</label>
                    <div className="w-28 shrink-0">
                      <DatePickerField
                        value={jobData.clientNotifiedAt || ''}
                        onChange={(dateStr) => handleFieldChange('clientNotifiedAt', dateStr)}
                        placeholder="dd.mm.yyyy"
                        inputClass="w-full bg-gray-700 border border-gray-600 rounded px-1.5 py-1 text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 min-w-0 flex-1 basis-0">
                    <span className="text-xs text-gray-400 shrink-0">Status:</span>
                    <span className="bg-gray-700 rounded px-1.5 py-0.5 text-xs text-gray-300 inline-flex items-center gap-1 truncate" title={markerRokovi?.label || ''}>
                      {markerRokovi ? (
                        <>
                          {markerRokovi.shortText || markerRokovi.shortLabel}
                          <DeadlineMarker job={mockJobRokovi} size="sm" />
                        </>
                      ) : '—'}
                    </span>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-700 flex flex-wrap items-center gap-x-4 gap-y-2 w-full">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1 basis-0">
                    <span className="text-xs text-gray-400 shrink-0">Kreirano:</span>
                    <span className="text-xs text-gray-300">{formatDateTime(jobData.createdAt)}</span>
                  </div>
                  {jobData.creator && (
                    <div className="flex items-center gap-1.5 min-w-0 flex-1 basis-0">
                      <span className="text-xs text-gray-400 shrink-0">Kreirao:</span>
                      <span className="text-xs text-gray-300 truncate">{jobData.creator}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 min-w-0 flex-1 basis-0">
                    <span className="text-xs text-gray-400 shrink-0">Ažurirano:</span>
                    <span className="text-xs text-gray-300">{formatDateTime(jobData.updatedAt)}</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Form buttons */}
      <div className="flex w-full gap-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSubmitting || jobState.jobSaving}
        >
          {isSubmitting || jobState.jobSaving ? t('common.saving') : (isEdit ? t('jobs.updateJob') : t('jobs.createJob'))}
        </button>
      </div>
      {/* Device images modal */}
      <input
        ref={deviceImagesQuickInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          handleDeviceImagesAdd(e.target.files);
          e.target.value = '';
          setShowDeviceImagesModal(true);
        }}
      />

      {showDeviceQrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-2xl w-full">
            <div className="flex items-center justify-between border-b border-gray-700 px-4 py-2">
              <h3 className="text-sm font-semibold text-white">QR aparata</h3>
              <button
                type="button"
                onClick={() => setShowDeviceQrModal(false)}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded"
              >
                Zatvori
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-gray-300">{qrScanStatus}</p>
              <video
                ref={qrVideoRef}
                className="w-full max-h-72 rounded border border-gray-700 bg-black"
                muted
                playsInline
                autoPlay
              />
              <div className="space-y-2">
                <label className="text-xs text-gray-400 block">
                  Ako kamera ne radi, nalepite sadržaj QR koda ručno:
                </label>
                <textarea
                  value={qrManualValue}
                  onChange={(e) => setQrManualValue(e.target.value)}
                  rows={3}
                  className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-sm text-white"
                  placeholder='Primer: {"brand":"Gorenje","model":"WNEI74SPS","serialNumber":"SN123456"}'
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded"
                    onClick={() => {
                      const ok = applyDeviceQrData(qrManualValue);
                      if (ok) {
                        toast.success('QR podaci su uspešno primenjeni.');
                        setShowDeviceQrModal(false);
                        setQrManualValue('');
                      } else {
                        toast.warning('QR tekst nema prepoznata polja (model/serijski/brend).');
                      }
                    }}
                  >
                    Primeni QR podatke
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeviceImagesModal && canUseJobImages && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-700 px-4 py-2">
              <h3 className="text-sm font-semibold text-white">
                Slike aparata ({jobData.deviceImages?.length || 0} / {maxJobImages})
              </h3>
              <button
                type="button"
                onClick={() => setShowDeviceImagesModal(false)}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded"
              >
                Potvrdi
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-auto">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-300">
                  <span>Dodaj slike aparata</span>
                  <span className="block sm:inline sm:ml-1 text-[11px] text-gray-400">
                    (maks. {maxJobImages} slika za vaš paket)
                  </span>
                </p>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    id="device-images-upload-modal"
                    className="hidden"
                    onChange={(e) => {
                      handleDeviceImagesAdd(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <label
                    htmlFor="device-images-upload-modal"
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded cursor-pointer"
                  >
                    Dodaj slike
                  </label>
                </div>
              </div>

              {jobData.deviceImages && jobData.deviceImages.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {jobData.deviceImages.map((img, idx) => (
                    <div key={idx} className="relative border border-gray-700 rounded-md overflow-hidden bg-black/40">
                      <img
                        src={img}
                        alt={`Aparat ${idx + 1}`}
                        className="w-full h-32 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleDeviceImageRemove(idx)}
                        className="absolute top-0 right-0 bg-black/70 text-white text-[10px] px-1.5 py-0.5"
                      >
                        Ukloni
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">
                  Još uvek nema priloženih slika. Kliknite na „Dodaj slike“ da ih dodate.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default JobForm; 